/**
 * Created by Qingger Corp.
 * User: jsspf
 * Date: 2017/11/21
 * Time: 12:31
 */
import { resolve } from 'url';
import axios from 'axios';
import merge from 'lodash/merge';
import type { BaseHttpRequestOption, BaseHttpRequestParams } from '@/util/type';
import { HttpMethod } from '@/util/type';
import type { PromiseAPICallFun } from '@/services/base/baseAPIService';
import { default as baseAPIService } from '@/services/base/baseAPIService';
import { EmpServiceAPIActions } from '@/services/empService';
import { IPAPIConfig } from '@/services/ipsServices/config';
import { JsonToUrlParam } from '@/util';
import { getActId, setActId } from '@/library/cache/storageCacheManager';
/**
 * 尊享店期刊API接口功能
 */
export namespace IPSService {
	import requireToken = EmpServiceAPIActions.requireEmpToken;

	function apiWithTokenLoader<T = any>(httpRequestOptions?: BaseHttpRequestOption): PromiseAPICallFun<T> {
		return baseAPIService.apiServiceLoader(httpRequestOptions, requireToken);
	}
	const downloadState = +localStorage.getItem('downloadState');
	function apiWithTokenLoaderExport<T = any>(httpRequestOptions?: BaseHttpRequestOption): PromiseAPICallFun<T> {
		if (downloadState === 2) {
			return baseAPIService.apiServiceLoaderExport(httpRequestOptions, requireToken);
		} else {
			return baseAPIService.apiServiceLoader(httpRequestOptions, requireToken);
		}
	}
	function crmApi<T = any>(httpRequestOptions?: BaseHttpRequestOption): PromiseAPICallFun<T> {
		return function (apiParams: BaseHttpRequestParams = {}) {
			const siteId = +localStorage.getItem('firstSiteId');
			const tokenData = {
				Authorization: `Bearer ${EmpServiceAPIActions.getTokenFromCache().token}`,
			};
			const params: any = {
				ext_ips__auth_token: tokenData.Authorization,
				ext_ips__site: siteId,
			};

			apiParams.headerParams = merge({}, apiParams.headerParams, params);
			return baseAPIService.apiCallWithToken(httpRequestOptions, apiParams);
		};
	}

	/**
	 * 需要使用siteId参数的接口，使用该方法
	 * @param {BaseHttpRequestOption} urlConfig
	 * @returns {Promise<any>}
	 */
	function apiClientCrmLoader<T = any>(urlConfig?: BaseHttpRequestOption): PromiseAPICallFun<T> {
		return function (apiParams: BaseHttpRequestParams = {}) {
			return AssessActId().then((actId) => {
				const siteId = +localStorage.getItem('siteId');
				const tokenData = {
					Authorization: `Bearer ${EmpServiceAPIActions.getTokenFromCache().token}`,
				};
				const params: any = {
					ext_ips__auth_token: tokenData.Authorization,
					ext_ips__site: siteId,
				};
				const str = JsonToUrlParam(params);
				const _url = Object.assign({}, urlConfig);
				if (_url.path.match(/{siteId}/)) {
					_url.path = _url.path.replace(/{siteId}/, `&${str}`);
				} else {
					if (_url.method == HttpMethod.GET) {
						if (!apiParams.queryParams) {
							apiParams.queryParams = {};
						}
						apiParams.queryParams = merge({}, apiParams.queryParams, {
							actId,
						});
					} else if (_url.method == HttpMethod.POST) {
						if (!apiParams.postParams) {
							apiParams.postParams = {};
						}
						apiParams.postParams = merge({}, apiParams.postParams, {
							actId,
						});
					}
				}
				if (!apiParams.headerParams) {
					apiParams.headerParams = {};
				}
				apiParams.headerParams = merge({}, apiParams.headerParams, params);
				return baseAPIService.apiCallWithToken(_url, apiParams);
			});
		};
	}
	export function AssessActId(reload = false): Promise<any> {
		const siteId = +localStorage.getItem('siteId');
		return new Promise((resolve) => {
			if (getActId() && !reload) {
				resolve(getActId());
			} else {
				siteGroupConfig({
					postParams: {
						siteId,
					},
				}).then((actInfo) => {
					setActId(actInfo.actId);
					resolve(actInfo.actId);
				});
			}
		});
	}
	/**
	 *AS31.保存网点组的商户号
	 * @see https://wiki.qingger.cn/pages/viewpage.action?pageId=11404042
	 */
	export const saveGroupMchId = (formData) => {
		return axios.post('/nips/admin/groupSite/mchId/save' + `?corpId=${baseAPIService.resolveCorpId()}`, formData.formData, {
			headers: {
				'Content-Type': 'multipart/form-data',
				'Authorization': `Bearer ${EmpServiceAPIActions.getTokenFromCache().token}`,
			},
		});
	};

	/**
	 * 系统Login
	 */
	function login() {
		requireToken().then((token: string) => {
			console.log('Login Success');
		});
	}

	/**
	 * AC10.编辑尺码对照表数据
	 * @type {PromiseAPICallFun<APIRetSuccess>}
	 */
	export const operateLevelClassSizeDesc = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.OPERATE_LEVEL_CLASS_SIZE_DESC);

	/**
	 * AS1. 获取网点组列表
	 * @type {PromiseAPICallFun<ListCountInfo<SiteGroupInfo>>}
	 */
	export const getSiteGroupList = apiWithTokenLoader<ListCountInfo<SiteGroupInfo>>(IPAPIConfig.APIResURLConfig.SITE_GROUP_LIST);
	/**
	 * COM.1 批量查询商品信息
	 * @see : https://runsa.yuque.com/runsadev/ir5wcy/xvb26m
	 */
	export const adminComBatchFindProducts = apiWithTokenLoader<ListCountInfo<SiteGroupInfo>>(IPAPIConfig.APIResURLConfig.ADMIN_COM_BATCH_FIND_PRODUCTS);
	/**
	 * COM.2 批量查询版单号关联商品
	 * @see : https://runsa.yuque.com/runsadev/ir5wcy/hcp7c8
	 */
	export const adminComBatchFindBDProducts = apiWithTokenLoader<ListCountInfo<SiteGroupInfo>>(IPAPIConfig.APIResURLConfig.ADMIN_COM_BATCH_FIND_BDPRODUCTS);
	/**
	 * AS2. 新增/编辑网点组
	 * 调用参数 {sgId,sgName,sgTitle,list}
	 * @type {PromiseAPICallFun<APIRetSuccess>}
	 */
	export const updateSiteGroup = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.UPDATE_SITE_GROUP);

	/**
	 * AS3. 网点组数据配置保存(基础配置)
	 * @type {PromiseAPICallFun<APIRetSuccess>}
	 */
	export const updateBasicConfig = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.UPDATE_BASIC_CONFIG);

	/**
	 * AS4. 网点组详情
	 * @type {PromiseAPICallFun<SiteGroupInfoOrg>}
	 */
	export const getSiteGroupInfoBySgId = apiWithTokenLoader<SiteGroupInfoOrg>(IPAPIConfig.APIResURLConfig.SITE_GROUP_INFO);

	/**
	 * AS5. 网点列表
	 * @type {PromiseAPICallFun<Array<OrgSiteTreeNodeInfo>>}
	 */
	export const getOrgSiteTreeList = apiWithTokenLoader<ListCountInfo<OrgSiteTreeNodeInfo>>(IPAPIConfig.APIResURLConfig.ORG_SITE_TREE_LIST);

	/**
	 * AS6. 删除网点组
	 * @type {PromiseAPICallFun<APIRetSuccess>}
	 */
	export const deleteSiteGroup = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.DELETE_SITE_GROUP);

	/**
	 * AS7. 获取网点组信息（网点上带有树型结构的管辖组织信息)
	 * @type {PromiseAPICallFun<SiteGroupInfoOrg>}
	 */
	export const getSiteGroupInfoOrg = apiWithTokenLoader<SiteGroupInfoOrg>(IPAPIConfig.APIResURLConfig.ORG_SITE_TREE_LIST_BY_SITE_GROUP);
/**
	 *AS61.批量店铺码生成导出
	 * @see https://runsa.yuque.com/runsadev/ir5wcy/tmgqyr
	 */
	export const adminSiteQRCodeExport = apiWithTokenLoader<{ list: exportSiteQRCode[] }>(IPAPIConfig.APIResURLConfig.ADMIN_SITE_QRCODE_EXPORT);
	/**
	 * AS8. 清除网点组的商品缓存
	 * @type {PromiseAPICallFun<APIRetSuccess>}
	 *
	 * @see:https://wiki.qingger.cn/pages/viewpage.action?pageId=11404321
	 */
	export const deleteSiteGroupCache = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.DELETE_SITE_GROUP_CACHE);

	/**
	 * AS8. 删除云镜店下某个网点关联信息
	 * @type {PromiseAPICallFun<APIRetSuccess>}
	 *
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=12747484
	 */
	export const removeSiteGroupInfo = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.DELETE_SITE_GROUP_INFO);
	/**
	 * AS8. 删除云镜店下某个网点关联信息
	 * @type {PromiseAPICallFun<APIRetSuccess>}
	 *
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=12747484
	 */
	export const removeSiteGroupInfoNode = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.DELETE_SITE_GROUP_INFO_NODE);

	/**
	 * AS9. 网点组数据配置详情(基础配置)
	 * @type {PromiseAPICallFun<BasicConfigInfo>}
	 */
	export const getBasicConfigInfo = apiWithTokenLoader<BasicConfigInfo>(IPAPIConfig.APIResURLConfig.BASIC_CONFIG_INFO);

	/**
	 * AS10. 网点组数据配置保存(页面配置)
	 * @type {PromiseAPICallFun<APIRetSuccess>}
	 */
	export const updatePageConfig = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.UPDATE_PAGE_CONFIG);

	/**
	 * AS11. 网点组数据配置详情(页面配置)
	 * @type {PromiseAPICallFun<PageConfigInfo>}
	 */
	export const getPageConfigInfo = apiWithTokenLoader<PageConfigInfo>(IPAPIConfig.APIResURLConfig.PAGE_CONFIG_INFO);

	/**
	 * AS16. 网点组数据配置选择(页面配置-期刊列表)
	 * @type {PromiseAPICallFun<ListCountInfo<PeriodicalByPageConfig>>}
	 */
	export const getPeriodicalListByPageConfig = apiWithTokenLoader<ListCountInfo<PeriodicalByPageConfig>>(IPAPIConfig.APIResURLConfig.PERIODICAL_LIST_BY_PAGE_CONFIG);

	/**
	 * AS17. 网点组数据配置选择(页面配置-活动列表)
	 * @type {PromiseAPICallFun<ListCountInfo<ActivityInfo>>}
	 */
	export const getActivityListByPageConfig = apiWithTokenLoader<ListCountInfo<ActivityInfo>>(IPAPIConfig.APIResURLConfig.ACTIVITY_LIST_BY_PAGE_CONFIG);

	/**
	 * AS18. 网点组数据配置选择(页面配置-商品列表)
	 * @type {PromiseAPICallFun<ListCountInfo<GoodsInfo>>}
	 */
	export const getGoodsListByPageConfig = apiWithTokenLoader<ListCountInfo<GoodsInfo>>(IPAPIConfig.APIResURLConfig.GOODS_LIST_BY_PAGE_CONFIG);

	/**
	 * AS19. 网点组数据配置选择(页面配置-网点筛选)
	 * @type {PromiseAPICallFun<ListCountInfo<SiteInfo>>}
	 */
	export const getSiteListByPageConfig = apiWithTokenLoader<ListCountInfo<SiteInfo>>(IPAPIConfig.APIResURLConfig.SITE_LIST_BY_PAGE_CONFIG);

	/**
	 * AS20. 根据店铺ID获取导购列表
	 * @type {PromiseAPICallFun<ListCountInfo<GuiderInfo>>}
	 */
	export const getGuiderListBySiteCode = apiWithTokenLoader<ListCountInfo<GuiderInfo>>(IPAPIConfig.APIResURLConfig.GUIDER_LIST_BY_SITE_CODE);

	/**
	 * AS22. 网点组拷贝
	 * @type {PromiseAPICallFun<APIRetSuccess>}
	 */
	export const copySiteGroupInfo = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.COPY_SITE_GROUP_INFO);

	/**
	 * AS24. 网点设定列表
	 * @type {PromiseAPICallFun<APIRetSuccess>}
	 */
	export const sitesArray = apiWithTokenLoader<ListCountInfo<ItemObject>>(IPAPIConfig.APIResURLConfig.SITE_ARRAY_INFO);

	/**
	 * AS69.网点设定列表
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/1052
	 */
	export const siteManager = apiWithTokenLoader<ListCountInfo<ItemObject>>(IPAPIConfig.APIResURLConfig.SITE_MANAGER);

	/**
	 *AS25.网点设定详情
	 * @type {PromiseAPICallFun<APIRetSuccess>}
	 */
	export const siteInfo = apiWithTokenLoader<SiteInfo>(IPAPIConfig.APIResURLConfig.SITE_INFO);

	/**
	 *AS26.网点设定保存
	 * @type {PromiseAPICallFun<APIRetSuccess>}
	 */
	export const saveSiteEdit = apiWithTokenLoader<SiteInfo>(IPAPIConfig.APIResURLConfig.SAVE_SITE_EDIT);

	/**
	 * AS33.获取网点组的商品分类黑名单
	 * @type {PromiseAPICallFun<Array<ItemObject>>}
	 */
	export const productClassHide = apiWithTokenLoader<ListCountInfo<ItemObject>>(IPAPIConfig.APIResURLConfig.PRODUCT_CLASS_HIDE);

	/**
	 * AS34.保存网点组的商品分类黑名单
	 * @type {PromiseAPICallFun<APIRetSuccess>}
	 */
	export const saveSiteGroupProClassHide = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.SAVE_SITE_GROUP_PRO_CLASS_HIDE);

	/**
	 * AS35.获取网点组其他配置
	 * @type {PromiseAPICallFun<GetSiteGroupOtherConfig>}
	 */
	export const getSiteGroupOtherConfig = apiWithTokenLoader<GetSiteGroupOtherConfig>(IPAPIConfig.APIResURLConfig.GET_SITE_GROUP_OTHER_CONFIG);

	/**
	 * AS36.保存网点组其他配置
	 * @type {PromiseAPICallFun<APIRetSuccess>}
	 */
	export const saveSiteGroupOtherConfig = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.SAVE_SITE_GROUP_OTHER_CONFIG);

	/**
	 * AS42. 小程序添加网点列表接口
	 * @type {PromiseAPICallFun<APIRetSuccess>}
	 */
	export const siteGroupSiteList = apiWithTokenLoader<ListCountInfo<OrgSiteTreeNodeInfo>>(IPAPIConfig.APIResURLConfig.SITE_GROUP_SITE_LIST);

	/**
	 * AS43.获取集团的开票说明
	 * @type {PromiseAPICallFun<ItemObject>}
	 */
	export const invoiceNote = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.INVOICE_NOTE);

	/**
	 * AS44.更新集团开票说明
	 * @type {PromiseAPICallFun<APIRetSuccess>}
	 */
	export const invoiceNoteSave = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.INVOICE_NOTE_SAVE);

	/**
	 * AS45.获取集团最大佣金比例
	 * @type {PromiseAPICallFun<ItemObject>}
	 */
	export const maxCommission = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.MAX_COMMISSION);

	/**
	 * AS46.更新集团最大佣金比例
	 * @type {PromiseAPICallFun<ItemObject>}
	 */
	export const maxCommissionSave = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.MAX_COMMISSION_SAVE);

	/**
	 * AS47.获取商品图片取值配置
	 * @type {PromiseAPICallFun<ItemObject>}
	 */
	export const productImageConfig = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.PRODUCT_IMAGE_CONFIG);

	/**
	 * AS48.更新集团商品图片配置
	 * @type {PromiseAPICallFun<APIRetSuccess>}
	 */
	export const productImageConfigSave = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.PRODUCT_IMAGE_CONFIG_SAVE);

	/**
	 * AS55.更新会员等级相关配置
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=61407251
	 */
	export const adminMemberLevelConfigSave = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.ADMIN_MEMBER_LEVEL_CONFIG_SAVE);

	/**
	 * AS56.获取会员等级相关配置
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=61407254
	 */
	export const adminMemberLevelConfig = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.ADMIN_MEMBER_LEVEL_CONFIG);

	/**
	 * AS58. 允许店铺销售
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=100204574
	 */
	export const adminSiteOpen = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.ADMIN_SITE_OPEN);
	/**
	 * AS59. 禁止店铺销售
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=100204576
	 */
	export const adminSiteClose = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.ADMIN_SITE_CLOSE);
	/**
	 * AC1. 新增/编辑分类(层级分类)
	 * @type {PromiseAPICallFun<APIRetSuccess>}
	 */
	export const updateLevelClass = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.UPDATE_LEVEL_CLASS);
	export const updateLevelNodeClass = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.UPDATE_LEVEL_NODE_CLASS);

	/**
	 * AC1-1. 新增/编辑分类(标签分类)
	 * @type {PromiseAPICallFun<APIRetSuccess>}
	 */
	export const updateLabelClass = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.UPDATE_LABEL_CLASS);

	/**
	 * AC2. 禁用/启用/删除分类
	 * @type {PromiseAPICallFun<APIRetSuccess>}
	 */
	export const batchOperationClass = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.BATCH_OPERATION_CLASS);

	/**
	 * AC4. 获取分类列表(层级)
	 * @type {PromiseAPICallFun<ListCountInfo<ProductClassInfo>>}
	 */

	export const getLevelClassList = apiWithTokenLoader<ListCountInfo<ProductClassInfo>>(IPAPIConfig.APIResURLConfig.LEVEL_CLASS_LIST);

	/**
	 * AC5. 获取分类列表(标签)
	 * @type {PromiseAPICallFun<ListCountInfo<ProductClassInfo>>}
	 */
	export const getLabelClassList = apiWithTokenLoader<ListCountInfo<ProductClassInfo>>(IPAPIConfig.APIResURLConfig.LABEL_CLASS_LIST);

	/**
	 * AC6. 分类关联属性
	 * @type {PromiseAPICallFun<APIRetSuccess>}
	 */
	export const relateAttributeOfClass = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.RELATE_ATTRIBUTE_OF_CLASS);

	/**
	 * AC7. 获取属性列表
	 * @type {PromiseAPICallFun<any>}
	 */
	export const getAttributeList = apiWithTokenLoader<ListCountInfo<RelationClass>>(IPAPIConfig.APIResURLConfig.ATTRIBUTE_LIST);

	/**
	 * AC8. 分类排序设置
	 * @type {PromiseAPICallFun<APIRetSuccess>}
	 */
	export const updateClassSort = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.UPDATE_CLASS_SORT);

	/**
	 * AC9. 新增层级分类下拉框
	 * @type {PromiseAPICallFun<ListCountInfo<ProductClassInfo>>}
	 */
	export const getTopClassList = apiWithTokenLoader<ListCountInfo<ProductClassInfo>>(IPAPIConfig.APIResURLConfig.CATEGORY_PARENT_LIST);

	/**
	 * Q1. 根据参数获取期刊列表
	 * @type {PromiseAPICallFun<ListCountInfo<QiKanDistributeInfo>>}
	 */
	export const getPeriodicalList = apiWithTokenLoader<ListCountInfo<QiKanDistributeInfo>>(IPAPIConfig.APIResURLConfig.ADMIN_QIKAN_LIST);

	/**
	 * Q2. 期刊预览
	 * @type {PromiseAPICallFun<Preview>}
	 */
	export const getPeriodicalPreview = apiWithTokenLoader<Preview>(IPAPIConfig.APIResURLConfig.PERIODICAL_PREVIEW);

	/**
	 * Q3. 期刊启用禁用
	 * @type {PromiseAPICallFun<APIRetSuccess>}
	 */
	export const UpdatePeriodicalStatus = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.UPDATE_PERIODICAL_STATUS);

	/**
	 * Q4. 期刊评论列表
	 * @type {boolean}
	 */
	export const getCommentList = apiWithTokenLoader<ListCountInfo<QiKanCommentInfo>>(IPAPIConfig.APIResURLConfig.PERIODICAL_COMMENT_LIST);

	/**
	 * Q5. 评论显示设置
	 * @type {PromiseAPICallFun<APIRetSuccess>}
	 */
	export const updateCommentStatus = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.UPDATE_COMMENT_STATUS);
	/**
	 * Q5. PQ17.期刊评论回复
	 * @type {PromiseAPICallFun<APIRetSuccess>}
	 */
	export const commentSave = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.COMMENT_SAVE);

	/**
	 * A1. 活动列表
	 * @type {PromiseAPICallFun<ListCountInfo<ActivityInfo>>}
	 */
	export const getActivityList = apiWithTokenLoader<ListCountInfo<InnerActivityList>>(IPAPIConfig.APIResURLConfig.ACTIVITY_LIST);
	export const getActivityPicInfo = apiWithTokenLoader<any>(IPAPIConfig.APIResURLConfig.ACTIVITY_PIC_INFO);
	export const getActivityPicSave = apiWithTokenLoader<any>(IPAPIConfig.APIResURLConfig.ACTIVITY_PIC_SAVE);


	/**
	 * A2. 活动启用/禁用
	 * @type {PromiseAPICallFun<APIRetSuccess>}
	 */
	export const updateActivityStatus = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.UPDATE_ACTIVITY_STATUS);

	/**
	 *A30.批量设置是否对外展示
	 * @type {PromiseAPICallFun<HttpAPIResultSuccess<boolean>>}
	 */
	export const actVendorState = apiWithTokenLoader<HttpAPIResultSuccess<boolean>>(IPAPIConfig.APIResURLConfig.ACT_VENDOR_STATE);

	/**
	 * A3.报名列表标题
	 * @type {PromiseAPICallFun<ListCountInfo<HeadColumn>>}
	 */
	export const getTitleList = apiWithTokenLoader<ListCountInfo<HeadColumn>>(IPAPIConfig.APIResURLConfig.TITLE_LIST);

	/**
	 * A4. 活动预览
	 * @type {PromiseAPICallFun<any>}
	 */
	export const getActivityPreview = apiWithTokenLoader<Preview>(IPAPIConfig.APIResURLConfig.ACTIVITY_PREVIEW);

	/**
	 * A5. 报名列表
	 * @type {PromiseAPICallFun<ListCountInfo<Enrolment>>}
	 */
	export const getPersonList = apiWithTokenLoader<ListCountInfo<Enrolment>>(IPAPIConfig.APIResURLConfig.REGISTERED_PERSON_LIST);

	/**
	 * A5_1.报名抽取列表
	 * @type {PromiseAPICallFun<ListCountInfo<Enrolment>>}
	 */
	export const getExtractionList = apiWithTokenLoader<ListCountInfo<Enrolment>>(IPAPIConfig.APIResURLConfig.REGISTERED_EXTRACTION_LIST);

	/**
	 * A5_2.发送抽中通知
	 * @type {PromiseAPICallFun<ListCountInfo<Enrolment>>}
	 */
	export const actNotice = apiWithTokenLoader<ListCountInfo<Enrolment>>(IPAPIConfig.APIResURLConfig.POST_ACT_NOTICE);

	/**
	 * A5_4.发送未抽中通知
	 * @type {PromiseAPICallFun<ListCountInfo<Enrolment>>}
	 */
	export const unLotteryNotice = apiWithTokenLoader<ListCountInfo<Enrolment>>(IPAPIConfig.APIResURLConfig.POST_ACT_NOTICE_UNLOTTERY);

	/**
	 * A5_3.获取抽取显示详情数据
	 * @type {PromiseAPICallFun<ListCountInfo<Enrolment>>}
	 */
	export const getExtractionNum = apiWithTokenLoader<ListCountInfo<Enrolment>>(IPAPIConfig.APIResURLConfig.REGISTERED_EXTRACTION_NUM);

	/**
	 * A5_5.获取该店铺其他活动列表
	 * @type {PromiseAPICallFun<ListCountInfo<Enrolment>>}
	 */
	export const getOtherActivityList = apiWithTokenLoader<ListCountInfo<OtherActivityList>>(IPAPIConfig.APIResURLConfig.GET_OTHER_ACTIVITY_LIST);

	/**
	 * A6.活动配置列列表
	 * @type {PromiseAPICallFun<ListCountInfo<ActTypeColumns>>}
	 */
	export const getActTypeColumnList = apiWithTokenLoader<ListCountInfo<ActTypeColumns>>(IPAPIConfig.APIResURLConfig.ACTIVITY_SET_COLUMN_LIST);

	/**
	 * A7.活动类型详情
	 * @type {PromiseAPICallFun<ActTypeDetail>}
	 */
	export const getActTypeDetail = apiWithTokenLoader<ActTypeDetail>(IPAPIConfig.APIResURLConfig.GET_ACTIVITY_COLUMNS_INFO);

	/**
	 * A8.活动类型保存
	 * @type {PromiseAPICallFun<APIRetSuccess>}
	 */
	export const updateActTypeDetail = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.UPDATE_ACTIVITY_COLUMNS_IFO);

	/**
	 * A9.修改活动状态
	 * @type {PromiseAPICallFun<APIRetSuccess>}
	 */
	export const updateActTypeStatus = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.UPDATE_ACTIVITY_TYPE_STATUS);

	/**
	 * A10.活动类型列表
	 * @type {PromiseAPICallFun<ListCountInfo<ActType>>}
	 */
	export const getActTypeList = apiWithTokenLoader<ListCountInfo<ActType>>(IPAPIConfig.APIResURLConfig.GET_ACTIVITY_TYPE_LIST);

	/**
	 * A11.活动类型适用店铺详情
	 * @type {PromiseAPICallFun<ListCountInfo<SiteInfo>>}
	 */
	export const getActTypeApplySites = apiWithTokenLoader<ListCountInfo<SiteInfo>>(IPAPIConfig.APIResURLConfig.GET_ACTIVITY_TYPE_APPLY_SITES);

	/**
	 * A12.活动类型适用店铺修改
	 * @type {PromiseAPICallFun<APIRetSuccess>}
	 */
	export const updateActTypeApplySites = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.UPDATE_ACTIVITY_TYPE_APPLY_SITES);

	/**
	 * AS27. 获取集团收益分享时效
	 * @type {PromiseAPICallFun<GroupSetInfo>}
	 */
	export const getGroupSetInfo = apiWithTokenLoader<GroupSetInfo>(IPAPIConfig.APIResURLConfig.GET_GROUP_SETTING);

	/**
	 * AS28. 集团收益分享时效保存
	 * @type {PromiseAPICallFun<APIRetSuccess>}
	 */
	export const updateGroupSet = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.UPDATE_GROUP_SET);

	/**
	 * AS29.获取集团的商户号列表
	 * @type {PromiseAPICallFun<APIRetSuccess>}
	 */
	export const GroupMchIdList = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.GROUP_MCH_ID_LIST);

	/**
	 * AS30.获取网点组的商户号
	 * @see https://wiki.qingger.cn/pages/viewpage.action?pageId=11404020
	 */
	export const GroupMchIdInfo = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.GROUP_MCH_INFO);

	/**
	 *AS32.保存网点组的商户号
	 * @see https://wiki.qingger.cn/pages/viewpage.action?pageId=11404042
	 */
	export const GroupMchIdSave = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.SAVE_MCH_ID);

	/**
	 * A13.获取外部活动列表
	 * http://git.project.qingger.com/qingger/shop/blob/master/S2_Projects/P16_IPShop/backendAPI/APIDOC/activity_A13.md
	 */
	export const getCrmActList = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.GET_CRM_ACT_LIST);

	/**
	 * A14.获取外部活动详情(查看列表中的网点组列表也用这个)
	 * http://git.project.qingger.com/qingger/shop/blob/master/S2_Projects/P16_IPShop/backendAPI/APIDOC/activity_A14.md
	 */
	export const getCrmActInfo = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.GET_CRM_ACT_INFO);

	/**
	 * A15.外部活动保存
	 * http://git.project.qingger.com/qingger/shop/blob/master/S2_Projects/P16_IPShop/backendAPI/APIDOC/activity_A15.md
	 */
	export const postCrmActSave = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.GET_CRM_ACT_SAVE);

	/**
	 * A16.查看活动的二维码
	 * http://git.project.qingger.com/qingger/shop/blob/master/S2_Projects/P16_IPShop/backendAPI/APIDOC/activity_A16.md
	 */
	export const GetCrmActQrcode = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.GET_CRM_ACT_QRCODE);

	/**
	 * A17.从crm更新活动数据
	 * http://git.project.qingger.com/qingger/shop/blob/master/S2_Projects/P16_IPShop/backendAPI/APIDOC/activity_A17.md
	 */
	export const updateCrmAct = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.UPDATE_CRM_ACT);

	/**
	 * A18.修改活动状态
	 * http://git.project.qingger.com/qingger/shop/blob/master/S2_Projects/P16_IPShop/backendAPI/APIDOC/activity_A18.md
	 */
	export const updateCrmActState = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.UPDATE_CRM_ACT_STATE);

	/**
	 * A19.活动对应报名信息的筛选项(发送抽中通知用到)
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=42172469
	 */
	export const adminActOrderChooseList = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.ADMIN_ACT_ORDER_CHOOSE_LIST);

	/**
	 * A20.获取某个报名活动的会员等级
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=55312402
	 */
	export const adminActCstLevelList = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.ADMIN_ACT_CST_LEVEL_LIST);

	/**
	 * A21.报名抽取
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=55312402
	 */
	export const adminActExtractionSave = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.ADMIN_ACT_EXTRACTION_SAVE);

	/**
	 * T1.获取网点的试穿人数统计
	 * http://git.project.qingger.com/qingger/shop/blob/master/S2_Projects/P16_IPShop/backendAPI/APIDOC/tryOn_T1.md
	 */
	export const siteTryOnNum = apiWithTokenLoader<ListCountInfo<tryOnItem>>(IPAPIConfig.APIResURLConfig.SITE_TRY_ON_NUM);

	/**
	 * T2.获取网点的试穿人数详情统计
	 * http://git.project.qingger.com/qingger/shop/blob/master/S2_Projects/P16_IPShop/backendAPI/APIDOC/tryOn_T2.md
	 */
	export const siteTryOnNumByTime = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SITE_TRY_ON_NUM_BY_TIME);

	/**
	 * T3.获取网点的试穿商品排名
	 * http://git.project.qingger.com/qingger/shop/blob/master/S2_Projects/P16_IPShop/backendAPI/APIDOC/tryOn_T3.md
	 */
	export const siteTryOnTop = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SITE_TRY_ON_TOP);

	export const mpAuthApi = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.SITE_TRY_ON_TOP);

	/**
	 * R1.报名审核列表
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=11405458
	 */
	export const guiderRequestList = apiWithTokenLoader<ListCountInfo<UserList>>(IPAPIConfig.APIResURLConfig.GUIDER_REQUEST_LIST);

	/**
	 * R2.报名详情
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=11405462
	 */
	export const guiderRequestInfo = apiWithTokenLoader<VirtualManagerInfo>(IPAPIConfig.APIResURLConfig.GUIDER_REQUEST_INFO);

	/**
	 * R3.虚拟店长审核
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=11405464
	 */
	export const guiderRequestSave = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.GUIDER_REQUEST_SAVE);

	/**
	 * R4.虚拟店长移除黑名单
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=11405466
	 */
	export const requestRemoveBlacklist = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.REQUEST_REMOVE_BLACKLIST);

	/**
	 * R5.虚拟店长预定义拒绝/提现理由列表
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=12748382
	 */
	export const rejectContentList = apiWithTokenLoader<ListCountInfo<ItemObject>>(IPAPIConfig.APIResURLConfig.REJECT_CONTENT_LIST);

	/**
	 * R6.虚拟店长：报名申请列表导出
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=12748672
	 */
	export const guiderRequestExport = apiWithTokenLoaderExport<APIRetSuccess>(IPAPIConfig.APIResURLConfig.GUIDER_REQUEST_EXPORT);

	/**
	 * T4.获取集团顾客试穿和导购出样配置信息
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=12747588
	 */
	export const getGroupConfig = apiWithTokenLoader<GroupConfig>(IPAPIConfig.APIResURLConfig.GET_GROUP_CONFIG);

	/**
	 * T4.获取集团顾客试穿和导购出样配置信息
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=12747588
	 */
	export const saveGroupConfig = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.SAVE_GROUP_CONFIG);

	/**
	 * VGC1.获取虚拟店长策略列表信息
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=12747702
	 */
	export const guiderConfigList = apiWithTokenLoader<ListCountInfo<DistributeStrategy>>(IPAPIConfig.APIResURLConfig.GUIDER_CONFIG_LIST);

	/**
	 * VGC2.删除虚拟店长策略信息
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=12747740
	 */
	export const guiderConfigDelete = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.GUIDER_CONFIG_DELETE);

	/**
	 * VGC3.新增虚拟店长策略信息
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=12747740
	 */
	export const guiderConfigAdd = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.GUIDER_CONFIG_ADD);

	/**
	 * VGC4.编辑虚拟店长策略信息
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=12747812
	 */
	export const guiderConfigEdit = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.GUIDER_CONFIG_EDIT);

	/**
	 * VGC5.获取虚拟店长策略信息
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=12747812
	 */
	export const guiderConfigInfo = apiWithTokenLoader<StrategyInfo>(IPAPIConfig.APIResURLConfig.GUIDER_CONFIG_INFO);

	/**
	 * VG1.虚拟店长列表
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=11405468
	 */
	export const guiderList = apiWithTokenLoader<ListCountInfo<DistributorInfo>>(IPAPIConfig.APIResURLConfig.GUIDER_LIST);

	/**
	 * VG2.虚拟店长分销历史
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=11405473
	 */
	export const guiderProfitInList = apiWithTokenLoader<ListCountInfo<DistributeHistory>>(IPAPIConfig.APIResURLConfig.GUIDER_PROFIT_IN_LIST);

	/**
	 * VG3.虚拟店长提现历史
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=11405485
	 */
	export const guiderProfitOutList = apiWithTokenLoader<ListCountInfo<WithDrawHistory>>(IPAPIConfig.APIResURLConfig.GUIDER_PROFIT_OUT_LIST);

	/**
	 * VG4.提现列表
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=11405543
	 */
	export const guiderAllProfitOutList = apiWithTokenLoader<ListCountInfo<RemitList>>(IPAPIConfig.APIResURLConfig.GUIDER_ALL_PROFIT_OUT_LIST);

	/**
	 * VG4.提现列表
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=11405543
	 */
	export const guiderAllProfitOutReject = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.GUIDER_ALL_PROFIT_OUT_REJECT);

	/**
	 * VG6.虚拟店长提现审核通过
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=11405554
	 */
	export const guiderAllProfitOutPassed = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.GUIDER_ALL_PROFIT_OUT_PASSED);

	/**
	 * VG7.虚拟店长提现列表（所有虚拟店长）导出
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=12749394
	 */
	export const guiderAllProfitOutListExport = apiWithTokenLoaderExport<APIRetSuccess>(IPAPIConfig.APIResURLConfig.GUIDER_ALL_PROFIT_OUT_LIST_EXPORT);

	/**
	 * VG8.虚拟店长打款
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=14778490
	 */
	export const guiderAllProfitOutPay = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.GUIDER_ALL_PROFIT_OUT_LIST_EXPORT_PAY);

	/**
	 * VG11.虚拟店长列表导出
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=12749072
	 */
	export const guiderListExport = apiWithTokenLoaderExport<APIRetSuccess>(IPAPIConfig.APIResURLConfig.GUIDER_LIST_EXPORT);

	/**
	 * VG12.虚拟店长分销历史导出
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=12749196
	 */
	export const guiderProfitInListExport = apiWithTokenLoaderExport<APIRetSuccess>(IPAPIConfig.APIResURLConfig.GUIDER_PROFIT_IN_LIST_EXPORT);

	/**
	 * VG13.虚拟店长提现历史导出
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=12749202
	 */
	export const guiderProfitOutListExport = apiWithTokenLoaderExport<APIRetSuccess>(IPAPIConfig.APIResURLConfig.GUIDER_PROFIT_OUT_LIST_EXPORT);

	/**
	 * VG14.虚拟店长报表
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=16024829
	 */
	export const guiderReportList = apiWithTokenLoader<ListCountInfo<GuiderReportList>>(IPAPIConfig.APIResURLConfig.GUIDER_REPORT_LIST);

	/** VG37.分销结算报表
	 * http://yapi.tenyun.runsasoft.com/mock/68/admin/v/fx/profit/report/list
	 */
	export const successReportList = apiWithTokenLoader<ListCountInfo<SuccessReportList>>(IPAPIConfig.APIResURLConfig.SUCCESS_REPORT_LIST);
	/** VG37.分销结算报表
	 * http://yapi.tenyun.runsasoft.com/mock/68/admin/v/fx/profit/report/list
	 */
	export const profitReportList = apiWithTokenLoader<ListCountInfo<ProfileReportList>>(IPAPIConfig.APIResURLConfig.PROFIT_REPORT_LIST);
	/* VG38.分销结算报表导出
		http://yapi.tenyun.runsasoft.com/mock/68/admin/v/fx/profit/report/list/export
		 */
	export const profitReportListExport = apiWithTokenLoaderExport<APIRetSuccess>(IPAPIConfig.APIResURLConfig.PROFIT_REPORT_LIST_EXPORT);
	/* VG38.分销结算报表导出
		http://yapi.tenyun.runsasoft.com/mock/68/admin/v/fx/profit/report/list/export
		 */
	export const successReportListExport = apiWithTokenLoaderExport<APIRetSuccess>(IPAPIConfig.APIResURLConfig.SUCCESS_REPORT_LIST_EXPORT);

	/**
	 * VG15.虚拟店长押金管理列表
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=16024930
	 */
	export const guiderDepositOutList = apiWithTokenLoader<ListCountInfo<GuiderDepositOutList>>(IPAPIConfig.APIResURLConfig.GUIDER_DEPOSIT_OUT_LIST);

	/**
	 * VG16.拒绝虚拟店长押金退回
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=16024951
	 */
	export const guiderDepositPassed = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.GUIDER_DEPOSIT_PASSED);

	/**
	 * VG17.虚拟店长提现审核通过
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=16024984
	 */
	export const guiderDepositReject = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.GUIDER_DEPOSIT_REJECT);

	/**
	 * VG18.关闭虚拟店长
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=16025003
	 */
	export const guiderClose = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.GUIDER_CLOSE);

	/**
	 *
	 * @type {PromiseAPICallFun<APIRetSuccess>}
	 */
	export const guiderOpen = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.GUIDER_OPEN);

	/**
	 * VG20.虚拟店长统计列表
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=40730695
	 */
	export const reportVGuiderList = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.REPORT_GUIDER_LIST);
	/**
	 * VG43.分销员成交明细
	 * https://yapi.tenyun.runsasoft.com/project/68/interface/api/5453
	 */
	export const reportVGuiderDetailList = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.REPORT_GUIDER_DETAIL_LIST);
	/**
	 * V40.分销员账户余额是否可以调整
	 * https://yapi.tenyun.runsasoft.com/project/68/interface/api/4785
	 */
	export const vGuiderChangeBalanceCheck = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.GUIDER_CHANGE_BALANCE_CHECK);
	/**
	 * V41.分销员账户余额的调整
	 * https://yapi.tenyun.runsasoft.com/project/68/interface/api/4776
	 */
	export const vGuiderChangeBalance = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.GUIDER_CHANGE_BALANCE);
	/**
	 * VG42.账户余额调整记录列表
	 * https://yapi.tenyun.runsasoft.com/project/68/interface/api/4794
	 */
	export const vGuiderChangeBalanceList = apiWithTokenLoader<ListCountInfo<ChangeHistory>>(IPAPIConfig.APIResURLConfig.GUIDER_CHANGE_BALANCE_LIST);

	/**
	 * VG1.虚拟店长列表
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=11405468
	 */
	// export const guiderList = apiWithTokenLoader<ListCountInfo<GuiderList>>(IPAPIConfig.APIResURLConfig.GUIDER_LIST);

	/**
	 * VG21.虚拟店长分销报表的分销历史
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=55312500
	 */
	export const reportProfitList = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.REPORT_PROFIT_LIST);

	/**
	 * VG22.虚拟店长分销报表提现历史
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=55312511
	 */
	export const reportProfitOutList = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.REPORT_PROFIT_OUT_LIST);

	/**
	 * VG23.虚拟店长分销报表列表导出
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=55476225
	 */
	export const reportDistributeBoardList = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.REPORT_DISTRIBUTE_BOARD_LIST);

	/**
	 * VG25.虚拟店长分销报表的提现历史导出
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=55934997
	 */
	export const adminGuiderProfitOutListExport = apiWithTokenLoaderExport<APIRetSuccess>(IPAPIConfig.APIResURLConfig.ADMIN_GUIDER_PROFIT_OUT_LIST_EXPORT);

	/**
	 * VG24.虚拟店长分销报表的分销历史导出
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=55934989
	 */
	export const adminGuiderProfitInListExport = apiWithTokenLoaderExport<APIRetSuccess>(IPAPIConfig.APIResURLConfig.ADMIN_GUIDER_PROFIT_IN_LIST_EXPORT);

	/**
	 * P1.分销商品列表
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=11405511
	 */
	export const guiderProductCommissionList = apiWithTokenLoader<ListCountInfo<GoodsList>>(IPAPIConfig.APIResURLConfig.GUIDER_PRODUCT_COMMISSION_LIST);

	/**
	 * P2.分销商品佣金保存
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=11405535
	 */
	export const guiderProductCommissionSave = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.GUIDER_PRODUCT_COMMISSION_SAVE);

	/**
	 * P3.分销商品佣金删除
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=11405537
	 */
	export const guiderProductCommissionRemove = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.GUIDER_PRODUCT_COMMISSION_REMOVE);

	/**
	 * P4.分销商品导入
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=14778501
	 */
	export const guiderProductCommissionImport = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.GUIDER_PRODUCT_COMMISSION_IMPORT);

	/**
	 * P5.分销商品导出
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=12749468
	 */
	export const guiderProductCommissionExport = apiWithTokenLoaderExport<APIRetSuccess>(IPAPIConfig.APIResURLConfig.GUIDER_PRODUCT_COMMISSION_EXPORT);

	/**
	 * AD1. 获取后台菜单列表
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=14778479
	 */
	export const adminAuthMenuList = apiWithTokenLoader<any>(IPAPIConfig.APIResURLConfig.ADMIN_AUTH_MENU_LIST);

	/**
	 * AQ3. 期刊店铺筛选数据
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=14779240
	 */
	export const qikanChooseSiteList = apiWithTokenLoader<any>(IPAPIConfig.APIResURLConfig.ADMIN_QIKAN_CHOOSE_SITE_LIST);

	/**
	 * SK1. 秒杀活动列表
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=19103800
	 */
	export const secKillList = apiWithTokenLoader<ListCountInfo<SeckillList>>(IPAPIConfig.APIResURLConfig.ADMIN_SEC_KILL_LIST);

	/**
	 * SK2. 秒杀活动修改状态
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=19103811
	 */
	export const secKillStateEdit = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.ADMIN_SEC_KILL_STATE_EDIT);

	/**
	 * SK3. 秒杀活动详情
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=19103816
	 */
	export const secKillInfo = apiWithTokenLoader<SecKillInfo>(IPAPIConfig.APIResURLConfig.ADMIN_SEC_KILL_INFO);
	/**
	 * AP32.批量查询商品信息
	 * https://yapi.tenyun.runsasoft.com/project/68/interface/api/5527
	 */
	export const searchProductList = apiWithTokenLoader<ItemArray>(IPAPIConfig.APIResURLConfig.ADMIN_SEARCH_PRODUCT_LIST);

	/**
	 * SK4. 新增秒杀活动
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=19103822
	 */
	export const secKillAdd = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.ADMIN_SEC_KILL_ADD);

	/**
	 * SK5. 编辑秒杀活动
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=19103831
	 */
	export const secKillSave = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.ADMIN_SEC_KILL_SAVE);

	/**
	 * SK6. 根据商品货号搜索商品
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=19529734
	 */
	export const productInfo = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.ADMIN_PRODUCT_INFO);

	/**
	 * SK7. 秒杀订单列表
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=19922983
	 */
	export const secKillOrderList = apiWithTokenLoader<ListCountInfo<OrderDetailList>>(IPAPIConfig.APIResURLConfig.ADMIN_SEC_KILL_ORDER_LIST);

	export const adminExport = apiWithTokenLoaderExport<ItemObject>(IPAPIConfig.APIResURLConfig.ADMIN_EXPORT);
	/**
	 * SK7.1. 秒杀订单列表导出
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=80675012
	 */
	// export const secKillOrderListExport = apiWithTokenLoaderExport<ListCountInfo<OrderDetailList>>(IPAPIConfig.APIResURLConfig.ADMIN_SEC_KILL_ORDER_LIST_EXPORT);

	/**
	 * SK8. 秒杀活动删除
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=19922983
	 */
	export const secKillDelete = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.ADMIN_SEC_KILL_DELETE);

	/**
	 * SK9. 根据网点获取秒杀活动列表
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=24739858
	 */
	export const secKillActList = apiWithTokenLoader<Array<SecKillActList>>(IPAPIConfig.APIResURLConfig.ADMIN_SEC_KILL_ACT_LIST);

	/**
	 * SK10. 查看秒杀活动的适用网点
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=28508232
	 */
	export const getSecKillSiteList = apiWithTokenLoader<ListCountInfo<SiteList>>(IPAPIConfig.APIResURLConfig.ADMIN_SEC_KILL_SITE_LIST);

	/**
	 * SK11. 秒杀活动规格明细
	 * https://wiki.qingger.cn/admin/sec/kill/spec/list?skId=2
	 */
	export const adminGoodsSkidList = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.ADMIN_GOODS_SKID_LIST);
	/**
	 *SK12.网点销售量-网点统计
	 *@see https://runsa.yuque.com/runsadev/ir5wcy/cb4xi8
	 */
	export const adminSecKillSIteOrderList = apiWithTokenLoader<any>(IPAPIConfig.APIResURLConfig.ADMIN_SEC_KILL_SITE_ORDER_LIST);

	/**
	 * AORD1. 后台订单列表
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=22249484
	 */
	export const orderList = apiWithTokenLoader<ListCountInfo<OrderList>>(IPAPIConfig.APIResURLConfig.ADMIN_ORDER_LIST);

	/**
	 * AORD2. 后台取消订单
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=22249488
	 */
	export const orderCancel = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.ADMIN_ORDER_CANCEL);
	// 开票
	export const orderInvoice = crmApi<HttpAPIRetItem>(IPAPIConfig.APIResURLConfig.ADMIN_ORDER_INVOICE);
	/* AORD7.后台订单管理 线下开票标记
		https://yapi.tenyun.runsasoft.com/project/68/interface/api/3796
		*/
	export const orderOfflineInvoicingSign = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.ADMIN_ORDER_OFFLINE_INVOICING_SIGN);

	/**
	 * AORD3. 后台订单发起退款操作
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=22249491
	 */
	export const orderReturn = apiWithTokenLoader<OrderReturn>(IPAPIConfig.APIResURLConfig.ADMIN_ORDER_RETURN);

	/**
	 * AORD4. 后台订单详情
	 */
	export const getOrderDetail = apiWithTokenLoader<OrderReturn>(IPAPIConfig.APIResURLConfig.ADMIN_ORDER_DETAIL);

	export const orderReturnSave = apiWithTokenLoader<OrderReturn>(IPAPIConfig.APIResURLConfig.ADMIN_ORDER_RETURN_SAVE);

	export const orderStateSwitch = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.ADMIN_ORDER_STATE_SWITCH);
	/**
	 * AS53.获取虚拟店长相关配置
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=32538651
	 */
	export const getVGuiderBaseConfig = apiWithTokenLoader<VGuiderBaseConfig>(IPAPIConfig.APIResURLConfig.GET_VGUIDER_BASE_CONFIG);

	/**
	 * AS52.更新虚拟店长相关配置
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=32538649
	 */
	export const setVGuiderBaseConfig = apiWithTokenLoader<OrderReturn>(IPAPIConfig.APIResURLConfig.SET_VGUIDER_BASE_CONFIG_SAVE);

	/**
	 * AS50.基于某一大类存储尺码对照表数据
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=32079917
	 */
	export const setAdminClassSizeSave = apiWithTokenLoader<OrderReturn>(IPAPIConfig.APIResURLConfig.SET_ADMIN_CLASS_SIZE_SAVE);

	/**
	 * AS50.基于某一大类存储尺码对照表数据
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=32079917
	 */
	export const getAdminClassSize = apiWithTokenLoader<OrderReturn>(IPAPIConfig.APIResURLConfig.GET_ADMIN_CLASS_SIZE);


	/**
	 * PQ1. 获取后台发布期刊列表
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=38371356
	 */
	export const adminPublishQiKanList = apiWithTokenLoader<OrderReturn>(IPAPIConfig.APIResURLConfig.ADMIN_PUBLISH_QIKAN_LIST);

	/**
	 * PQ2. 发布期刊修改状态
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=38371368
	 */
	export const adminPublishQiKanState = apiWithTokenLoader<OrderReturn>(IPAPIConfig.APIResURLConfig.ADMIN_PUBLISH_QIKAN_STATE);

	/**
	 * PQ3. 发布/编辑期刊
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=38371373
	 */
	export const adminPublishQiKanEdit = apiWithTokenLoader<OrderReturn>(IPAPIConfig.APIResURLConfig.ADMIN_PUBLISH_QIKAN_EDIT);

	/**
	 * PQ4. 根据商品货号搜索商品
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=38731783
	 */
	export const adminProductQiKanInfo = apiWithTokenLoader<OrderReturn>(IPAPIConfig.APIResURLConfig.ADMIN_PRODUCT_QIKAN_INFO);

	/**
	 * PQ5. 期刊详情
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=38731783
	 */
	export const adminPublishQiKanInfo = apiWithTokenLoader<OrderReturn>(IPAPIConfig.APIResURLConfig.ADMIN_PUBLISH_QIKAN_INFO);

	/**
	 * PQ6.后台期刊发布按钮
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=40730713
	 */
	export const adminPublishQiKanStatus = apiWithTokenLoader<OrderReturn>(IPAPIConfig.APIResURLConfig.ADMIN_PUBLISH_QIKAN_STATUS);

	/**
	 * PQ7.后台发布期刊选择
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=38731810
	 */
	export const adminPublishQiKanTypeChoose = apiWithTokenLoader<OrderReturn>(IPAPIConfig.APIResURLConfig.ADMIN_PUBLISH_QIKAN_TYPE_CHOOSE);

	/**
	 * PQ8.获取后台期刊热门标签
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=42172449
	 */
	export const adminPublishQiKanPopularLabel = apiWithTokenLoader<OrderReturn>(IPAPIConfig.APIResURLConfig.ADMIN_PUBLISH_QIKAN_LABEL);

	/**
	 * PQ9.后台 期刊预览
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=42172480
	 */
	export const adminPublishQiKanDisplay = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.ADMIN_PUBLISH_QIKAN_DISPLAY);

	/**
	 * PQ10. 选择网点
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=46399500
	 */
	export const adminPublishQiKanSiteList = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.ADMIN_PUBLISH_QIKAN_SITE_LIST);

	/**
	 * PQ12.网点期刊运营数据
	 * https://runsa.yuque.com/runsadev/ir5wcy/svpm2s
	 */
	export const qiKanSiteOperationInfo = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.ADMIN_QIKAN_SITE_OPERATION_INFO);

	/**
	 * PQ13.网点期刊运营数据(网点)
	 * https://runsa.yuque.com/runsadev/ir5wcy/zdeh7t
	 */
	export const qiKanAllSiteOperationInfo = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.ADMIN_PQIKAN_ALL_SITE_LIST);

	/*
	 * PQ14.网点期刊运营数据(网点)
	 * https://runsa.yuque.com/runsadev/ir5wcy/zdeh7t
	 */
	export const createQiKanUrl = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.ADMIN_PQIKAN_CREATE_QIKAN_URL);

	/**
	 * A1.1.活动列表导出
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=59277441
	 */
	export const adminActivityListExport = apiWithTokenLoaderExport<ItemObject>(IPAPIConfig.APIResURLConfig.ADMIN_ACTIVITY_LIST_EXPORT);

	/**
	 * VG9.虚拟店长线下结算
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=84869175
	 */
	export const offlineSettlement = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.OFFLINE_SETTLEMENT);

	/**
	 * VG26.二级明细
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=84606978
	 */
	export const childDetailList = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.CHILD_DETAIL_LIST);

	/**
	 * VG27.虚拟店长分销报表的所有分销历史导出
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=84607020
	 */
	export const exportProfitList = apiWithTokenLoaderExport<ItemObject>(IPAPIConfig.APIResURLConfig.EXPORT_PROFIT_LIST);

	/**
	 * PTA1. 后台拼团活动列表
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=82739249
	 */
	export const assembleActList = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.ASSEMBLE_ACT_LIST);

	/**
	 * PTA1.1.拼团活动列表导出
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=82739322
	 */
	export const PtProductListExport = apiWithTokenLoaderExport<ItemObject>(IPAPIConfig.APIResURLConfig.ADMIN_PT_PRODUCT_LIST_EXPORT);

	/**
	 * PTA2. 拼团活动结果跟踪(大区)
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=82739265
	 */
	export const assembleActResult = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.ASSEMBLE_ACT_RESULT);
	/**
	 * PTA2.1.拼团活动跟踪结果(大区)导出
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=82739325
	 */
	export const adminPTActSiteExport = apiWithTokenLoaderExport<ItemObject>(IPAPIConfig.APIResURLConfig.ADMIN_PT_ACT_SITE_EXPORT);

	/**
	 * PTA3. 拼团活动结果跟踪(网点)
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=82739270
	 */
	export const assembleActSiteOrderList = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.ASSEMBLE_ACT_SITE_ORDER_LIST);

	/**
	 * PTA4. 新增/编辑拼团活动
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=82739293
	 */
	export const editAssembleAct = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.EDIT_ASSEMBLE_ACT);
	/**
	 * PTA4. 新增/编辑拼团活动
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=82739293
	 */
	export const getAssembleAct = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.GET_ASSEMBLE_ACT);

	/**
	 *PTA5. 删除拼团活动
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=82739319
	 */
	export const deleteAssembleAct = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.DELETE_ASSEMBLE_ACT);

	/**
	 * PTA6. 更新活动状态
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=85950488
	 */
	export const updateAssembleActState = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.UPDATE_ASSEMBLE_ACT_STATE);

	/**
	 * PTA7. 查看拼团活动的适用网点
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=89554946
	 */
	export const ptActSiteChooseList = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.PT_ACT_SITE_CHOOSE_LIST);
	/**
	 * 网点树接口
	 */
	export const getAdminAuthTree = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.ADMIN_GET_AUTH_TREE_SITE);
	/**
	 * PTA8.生成拼团活动预览二维码
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=91226190
	 */
	export const getPtActPreviewCode = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.PT_ACT_PREVIEW_CODE_GET);
	/*
	 * ALV1. 获取微信直播列表
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=88965221
	 */
	export const wxLiveViewList = apiWithTokenLoader<ListCountInfo<WxLiveViewList>>(IPAPIConfig.APIResURLConfig.WX_LIVE_VIEW_LIST);

	/**
	 * ALV2. 后台直播列表
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=88965173
	 */
	export const liveViewList = apiWithTokenLoader<ListCountInfo<LiveViewList>>(IPAPIConfig.APIResURLConfig.LIVE_VIEW_LIST);

	/**
	 * ALV3. 直播销售店铺列表
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=88965198
	 */
	export const liveViewSiteList = apiWithTokenLoader<ListCountInfo<SaleSiteList>>(IPAPIConfig.APIResURLConfig.LIVE_VIEW_SITE_LIST);

	/**
	 * ALV4. 直播店铺销售列表
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=88965201
	 */
	export const liveViewSaleList = apiWithTokenLoader<ListCountInfo<SiteSaleList>>(IPAPIConfig.APIResURLConfig.LIVE_VIEW_SALE_LIST);

	/**
	 * ALV5. 修改直播状态
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=88965206
	 */
	export const liveViewStateSave = apiWithTokenLoader<HttpAPIRetResultSuccess>(IPAPIConfig.APIResURLConfig.LIVE_VIEW_STATE_SAVE);

	/**
	 * ALV6. 直播基础数据详情
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=89555031
	 */
	export const liveViewBaseInfo = apiWithTokenLoader<LiveViewBaseInfo>(IPAPIConfig.APIResURLConfig.LIVE_VIEW_BASE_INFO);

	/**
	 * ALV7. 直播基础数据保存
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=89555037
	 */
	export const liveViewBaseSave = apiWithTokenLoader<HttpAPIRetResultSuccess>(IPAPIConfig.APIResURLConfig.LIVE_VIEW_BASE_SAVE);

	/**
	 * ALV8. 同步微信直播商品
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=89555059
	 */
	export const liveViewProductsUpdate = apiWithTokenLoader<HttpAPIRetResultSuccess>(IPAPIConfig.APIResURLConfig.LIVE_VIEW_PRODUCTS_UPDATE);

	/**
	 * ALV9. 修改直播商品信息
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=89555063
	 */
	export const liveViewProductsEdit = apiWithTokenLoader<HttpAPIRetResultSuccess>(IPAPIConfig.APIResURLConfig.LIVE_VIEW_PRODUCTS_EDIT);

	/**
	 * ALV10. 直播宣传文案新增
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=89555070
	 */
	export const liveViewImagesAdd = apiWithTokenLoader<HttpAPIRetResultSuccess>(IPAPIConfig.APIResURLConfig.LIVE_VIEW_IMAGE_ADD);

	/**
	 * ALV11. 直播预览落地页
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=89555075
	 */
	export const liveViewDisplay = apiWithTokenLoader<HttpAPIRetResultSuccess>(IPAPIConfig.APIResURLConfig.LIVE_VIEW_DISPLAY);

	/**
	 * ALV12. 直播宣传文案删除
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=89555102
	 */
	export const liveViewImagesDel = apiWithTokenLoader<HttpAPIRetResultSuccess>(IPAPIConfig.APIResURLConfig.LIVE_VIEW_IMAGE_DEL);

	/**
	 * ALV13. 直播宣传文案排序
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=89555104
	 */
	export const liveViewImagesSort = apiWithTokenLoader<HttpAPIRetResultSuccess>(IPAPIConfig.APIResURLConfig.LIVE_VIEW_IMAGE_SORT);

	/**
	 * ALV14. 直播落地页详情
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=89555106
	 */
	export const liveViewInfo = apiWithTokenLoader<HttpAPIRetResultSuccess>(IPAPIConfig.APIResURLConfig.LIVE_VIEW_INFO);

	/**
	 * ALV15. 直播落地页发布
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=89555117
	 */
	export const liveViewPush = apiWithTokenLoader<HttpAPIRetResultSuccess>(IPAPIConfig.APIResURLConfig.LIVE_VIEW_PUSH);

	/**
	 * ALV16. 直播商品链接生成接口
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=89817090
	 */
	export const liveViewProductUrl = apiWithTokenLoader<ListCountInfo<VideoGoodsList>>(IPAPIConfig.APIResURLConfig.LIVE_VIEW_PRODUCT_URL);
	/**
	 * ALV17. 同步直播间信息
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=93290501
	 */
	export const liveViewUpdateRoomWX = apiWithTokenLoader<ListCountInfo<VideoGoodsList>>(IPAPIConfig.APIResURLConfig.LIVE_VIEW_UPDATE_ROOM_WX);

	/**
	 * ALV18.直播商品导入
	 *https://wiki.qingger.cn/pages/viewpage.action?pageId=102072371
	 */
	export const liveViewProductsImport = apiWithTokenLoader<ListCountInfo<VideoGoodsList>>(IPAPIConfig.APIResURLConfig.LIVE_VIEW_PRODUCTS_IMPORT);
	/**
	 * ALV20. 获取直播间商品列表
	 * https://yapi.tenyun.runsasoft.com/project/68/interface/api/5842
	 */
	export const liveViewProductsList = apiWithTokenLoader<ListCountInfo<VideoProductList>>(IPAPIConfig.APIResURLConfig.LIVE_VIEW_PRODUCT_LIST);
	/**
	 * ALV21. 下载商品讲解视频
	 * https://yapi.tenyun.runsasoft.com/project/68/interface/api/5851
	 *
	 */
	export const liveDownloadProductView = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.LIVE_DOWNLOAD_PRODUCT_VIEW);
	/**
	 * VG8.2 虚拟店长打款
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=94699552
	 */
	export const guiderBatchPayment = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.GUIDER_BATCH_PAYMENT);

	/**
	 * ZXA1. 主题颜色保存
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=96632850
	 */
	export const decorationThemeColorSave = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.DECORATION_THEME_COLOR_SAVE);
	/**
	 * ZXA1.1 主题颜色详情
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=96632850
	 */
	export const decorationThemeColorInfo = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.DECORATION_THEME_COLOR_INFO);

	/**
	 * ZXA2.装修的菜单导航保存
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=96632850
	 */
	export const decorationThemeNavSave = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.DECORATION_THEME_NAV_SAVE);

	/**
	 * ZXA3.2. hot页配置
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=96632850
	 */
	export const decorationThemeHotSave = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.DECORATION_THEME_HOT_SAVE);

	/**
	 * ZXA6.2 Hot页配置详情
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=97878056
	 */
	export const decorationThemeHotInfo = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.DECORATION_THEME_HOT_INFO);

	/**
	 * ZXA4.菜单配置详情
	 *https://wiki.qingger.cn/pages/viewpage.action?pageId=96632884
	 */
	export const decorationThemeNavInfo = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.DECORATION_THEME_NAV_INFO);
	/**
	 * ZXA13.开屏广告装修配置保存
	 * https://runsa.yuque.com/runsadev/ir5wcy/ivic13
	 */
	export const adminOpenAdcSave = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.ADMIN_OPEN_ADC_SAVE);
	/**
	 * AS107.保存相关网点组的配置信息
	 * http://yapi.tenyun.runsasoft.com/project/68/interface/api/8636
	 */
	export const adminSiteGroupConfigSave = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.ADMIN_SITE_GROUP_CONFIG_SAVE);
	/**
	 * ZXA14.开屏广告配置详情
	 * https://runsa.yuque.com/runsadev/ir5wcy/gy6993
	 */
	export const adminOpenAdcInfo = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.ADMIN_OPEN_ADC_INFO);
	/**
	 * SZX29.开屏广告列表
	 * http://yapi.tenyun.runsasoft.com/project/68/interface/api/8620
	 */
	export const adminOpenAdvList = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.ADMIN_OPEN_ADV_LIST);
	/**
	 * SZX30.开屏广告删除
	 * http://yapi.tenyun.runsasoft.com/project/68/interface/api/8640
	 */
	export const adminOpenAdvDelete = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.ADMIN_OPEN_ADV_DELETE);

	/**
	 * ZXA6. 商城页配置详情
	 *https://wiki.qingger.cn/pages/viewpage.action?pageId=96632947
	 */
	export const decorationMallInfo = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.DECORATION_MALL_INFO);
	/**
	 *ZXA3. 商城页配置
	 *https://wiki.qingger.cn/pages/viewpage.action?pageId=96632865
	 */
	export const decorationMallSave = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.DECORATION_MALL_SAVE);
	/**
	 * ZXA7.1独立店铺配置详情
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=98336860
	 */
	export const decorationSiteConfig = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.DECORATION_SITE_CONFIG);

	/**
	 * ZXA7.独立店铺配置
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=98336821
	 */
	export const decorationSiteConfigSave = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.DECORATION_SITE_SAVE);

	/**
	 * ZX-GS7.新增/编辑橱窗
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=96632970
	 */
	export const caseEdit = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.CASE_EDIT);
	/**
	 * ZX-GS18..获取已添加到橱窗位的店铺列表
	 * https://yapi.tenyun.runsasoft.com/project/68/interface/api/6832
	 */
	export const caseSiteCodes = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.CASE_SITE_CODES);

	/**
	 * ZX-GS8.橱窗详情
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=96632978
	 */
	export const caseInfo = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.CASE_INFO);

	/**
	 * ZX-GS9.删除橱窗
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=96632976
	 */
	export const caseDelete = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.CASE_DELETE);

	/**
	 * ZX-GS6.橱窗位的橱窗列表
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=96632963
	 */
	export const caseList = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.CASE_LIST);

	/**
	 * ZX-GS1.店铺展示管理左侧树菜单
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=96632887
	 */
	export const gsTreeList = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.GS_TREE_LIST);

	/**
	 * ZX-GS2.广告位的广告列表
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=96632893
	 */
	export const gsPropageateList = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.GS_PROPAGEATE_LIST);

	/**
	 * ZX-GS3.新增/编辑广告
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=96632901
	 */
	export const gsPropageateEdit = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.GS_PROPAGEATE_EDIT);

	/**
	 * ZX-GS4.广告详情
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=96632914
	 */
	export const gsPropageateInfo = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.GS_PROPAGEATE_INFO);

	/**
	 * ZX-GS5.设置广告状态
	 *https://wiki.qingger.cn/pages/viewpage.action?pageId=96632909
	 */
	export const gsPropageateStateUpdate = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.GS_PROPAGEATE_STATE_UPDATE);

	/**
	 * ZX-GS6.橱窗位的橱窗列表
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=96632963
	 */
	export const gsCaseList = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.GS_CASE_LIST);

	/**
	 * ZXA8独立店铺配置预览
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=100925479
	 */
	export const siteConfigPreview = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SITE_CONFIG_PREVIEW);

	/**
	 * A22.获取店铺促销活动列表
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=109314083
	 */
	export const getProActList = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.GET_PRO_ACT_LIST);

	/**
	 * CXA6.促销活动列表
	 * https://runsa.yuque.com/runsadev/ir5wcy/kg8x0o
	 */
	export const getPromotionList = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.PROMOTION_ACTIVITY_LIST);

	/**
	 * CXA7.促销活动修改状态/删除
	 * https://runsa.yuque.com/runsadev/ir5wcy/tlylpg
	 */
	export const delPromotion = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.DEL_PROMOTION);

	/**
	 * CXA4.促销活动装修详情
	 * https://runsa.yuque.com/runsadev/ir5wcy/tgeby5
	 */
	export const promotionInfo = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.PROMOTION_INFO);

	/**
	 * CXA3.新增/编辑促销活动
	 * https://runsa.yuque.com/runsadev/ir5wcy/kfcpz3
	 */
	export const editPromotion = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.EDIT_PROMOTION);

	/**
	 * CXA1.获取o2o后台促销活动
	 * https://runsa.yuque.com/runsadev/ir5wcy/ev5vr3
	 */
	export const getOMSPromotionInfo = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.GET_OMS_PROMOTION_INFO);

	/**
	 * CXA4.促销活动装修详情
	 * https://runsa.yuque.com/runsadev/ir5wcy/tgeby5
	 */
	export const promotionEditInfo = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.PROMOTION_EDIT_INFO);

	/**
	 * CXA5.促销活动装修保存
	 * https://runsa.yuque.com/runsadev/ir5wcy/vvfcb0
	 */
	export const savePromotionEditInfo = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SAVE_PROMOTION_EDIT_INFO);

	/**
	 * CXA8.后台促销预览
	 * https://runsa.yuque.com/runsadev/ir5wcy/aq1bg8
	 */
	export const promotionPreview = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.PROMOTION_PREVIEW);

	/**
	 * CXA7.促销活动修改状态/删除
	 * https://runsa.yuque.com/runsadev/ir5wcy/tlylpg
	 */
	export const promotionStateEdit = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.PROMOTION_STATE_EDIT);

	/**
	 * PQ16.期刊列表的预览
	 * https://runsa.yuque.com/runsadev/ir5wcy/dm8nyk
	 */
	export const qikanListPreview = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.QIKAN_LIST_PREVIEW);

	/**
	 * VG28.修改身份标识
	 * https://runsa.yuque.com/runsadev/ir5wcy/ttay4f
	 */
	export const updateIdentityTag = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.UPDATE_IDENTITY_TAG);

	/**
	 * VG29.虚拟店长导出数据模板1
	 * https://runsa.yuque.com/runsadev/ir5wcy/xuz48f
	 */
	export const vGuderTemplateOneOut = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.VGUIDER_TEMPLATE_ONE_OUT);

	/**
	 * VG30.虚拟店长导出数据模板2
	 * https://runsa.yuque.com/runsadev/ir5wcy/xbc60p
	 */
	export const vGuderTemplateTwoOut = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.VGUIDER_TEMPLATE_TWO_OUT);

	/**
	 * VG31.虚拟店长扣税金额导入
	 * https://runsa.yuque.com/runsadev/ir5wcy/yybu9k
	 */
	export const vGuiderTemplateImport = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.VGUIDER_TEMPLATE_IMPORT);

	/**
	 * TS1.获取运费策略列表
	 * https://runsa.yuque.com/runsadev/ir5wcy/xahszc
	 */
	export const getPostageList = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.GET_POSTAGE_LIST);

	/**
	 * TS1.2运费策略详情
	 * https://runsa.yuque.com/runsadev/ir5wcy/an5381
	 */
	export const getPostageInfo = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.GET_POSTAGE_INFO);

	/**
	 * TS1.3.新增/编辑运费策略
	 * https://runsa.yuque.com/runsadev/ir5wcy/fdf9wt
	 */
	export const editPostage = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.EDIT_POSTAGE);

	/**
	 * TS2.获取区域表
	 * https://runsa.yuque.com/runsadev/ir5wcy/sgzg2h
	 */
	export const getProvinceList = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.GET_PROVINCE_LIST);

	/**
	 * TS3.删除/禁用运费策略配置
	 * https://runsa.yuque.com/runsadev/ir5wcy/tofbpp
	 */
	export const deletePostage = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.DELETE_POSTAGE);

	/**
	 * ZXA11. 个人中心详情
	 * https://runsa.yuque.com/runsadev/ir5wcy/py9h5c
	 */
	export const getMemberCenterInfo = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.GET_MEMBER_CENTER_INFO);

	/**
	 * ZXA12. 个人中心保存
	 * https://runsa.yuque.com/runsadev/ir5wcy/ulrw96
	 */

	export const memberCenterSave = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.MEMBER_CENTER_SAVE);
	/**
	 * ZXA10.后台网点库存可提可售预览
	 * https://runsa.yuque.com/runsadev/ir5wcy/dizyzf
	 */
	export const getSiteStockInfo = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.GET_STOCK_INFO);
	/**
	 * 获取定制费的信息配置
	 * https://runsa.yuque.com/runsadev/ir5wcy/ep15970gnycgu3w5
	 */
	export const getCustomizeInfo = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.GET_CUSTOMIZE_INFO);
	/**
	 * ZXA10.后台网点库存可提可售预览
	 * https://runsa.yuque.com/runsadev/ir5wcy/dizyzf
	 */
	export const getSiteOtherConfigInfo = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.GET_SITE_OTHER_CONFIG_INFO);

	/**
	 * ZXA9.小程序后台网点库存可提可售配置
	 * https://runsa.yuque.com/runsadev/ir5wcy/gc4fdb
	 */
	export const saveSiteStock = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SAVE_SITE_STOCK);
	/**
	 * 保存定制费的信息
	 * https://runsa.yuque.com/runsadev/ir5wcy/xvv7v6rl7uupziyi
	 */
	export const customizeInfoSave = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.CUSTOMIZE_INFO_SAVE);
	/**
	 * AS98.店铺功能配置设置保存
	 * https://yapi.tenyun.runsasoft.com/project/68/interface/api/4376
	 */
	export const saveSiteOtherConfig = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SAVE_SITE_OTHER_CONFIG);

	/**
	 * PQ1.1后台期刊列表导出
	 * https://runsa.yuque.com/runsadev/ir5wcy/yzi424
	 */
	export const qiKanListExport = apiWithTokenLoaderExport<ItemObject>(IPAPIConfig.APIResURLConfig.QIKAN_LIST_EXPORT);

	/**
	 * ZAX15.微页面列表
	 * https://runsa.yuque.com/runsadev/ir5wcy/tfcz6d
	 */
	export const miniPageList = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.MINI_PAGE_LIST);
	/**
	 * ZXA16.微页面配置保存
	 * https://runsa.yuque.com/runsadev/ir5wcy/mrps6w
	 */
	export const miniPageSave = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.MINI_PAGE_SAVE);

	/**
	 * ZXA17.微页面配置详情
	 * https://runsa.yuque.com/runsadev/ir5wcy/kq32xi
	 */
	export const miniPageInfo = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.MINI_PAGE_INFO);
	/**
	 * ZXA18.删除微页面
	 * https://runsa.yuque.com/runsadev/ir5wcy/apcl8i
	 */
	export const miniPageDelete = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.MINI_PAGE_DEL);
	/**
	 * ZXA19.启用/禁用微页面
	 * https://runsa.yuque.com/runsadev/ir5wcy/wx6gpn
	 */
	export const miniPageUpdate = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.MINI_PAGE_UPDATE);

	/**
	 * AS2.1.获取CRM账套信息
	 * https://runsa.yuque.com/runsadev/ir5wcy/xhdame
	 */
	export const crmAccountList = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.CRM_ACCOUNT_LIST);

	/**
	 * ADN3.获取集团信息
	 * https://runsa.yuque.com/runsadev/ir5wcy/109674540
	 */
	export const getGroupInfo = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.GET_GROUP_INFO);
	/**
	 * ADN4.保存集团信息
	 * https://yapi.tenyun.runsasoft.com/project/68/interface/api/5649
	 */
	export const groupSave = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.GROUP_SAVE);

	/**
	 * ZXA20.微页面配置预览
	 * https://runsa.yuque.com/runsadev/ir5wcy/wndph8
	 */
	export const miniPagePreview = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.MINI_PAGE_PREVIEW);

	/**
	 * ZX-GS10.视频位的视频列表
	 * https://runsa.yuque.com/runsadev/ir5wcy/ruh3e1
	 */
	export const gsVideoList = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.GS_VIDEO_LIST);

	/**
	 * ZX-GS11.新增/编辑视频
	 * https://runsa.yuque.com/runsadev/ir5wcy/xad9ov
	 */
	export const gsVideoEdit = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.GS_VIDEO_EDIT);
	/**
	 * ZX-GS12.视频详情
	 * https://runsa.yuque.com/runsadev/ir5wcy/rviyp6
	 */
	export const gsVideoInfo = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.GS_VIDEO_INFO);
	/**
	 * ZX-GS13.操作视频上下架状态
	 * https://runsa.yuque.com/runsadev/ir5wcy/wmnvdc
	 */
	export const gsVideoStateUpdate = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.GS_VIDEO_STATE_UPDATE);
	/**
	 * ZX-GS14.删除视频
	 * https://runsa.yuque.com/runsadev/ir5wcy/wytl3y
	 */
	export const gsVideoDelete = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.GS_VIDEO_DELETE);

	/**
	 * ZXA21.微页面暂存
	 * https://runsa.yuque.com/runsadev/ir5wcy/nesq8x
	 */
	export const miniPageTempSave = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.MINI_PAGE_TEMP_SAVE);

	/**
	 * AS71.云镜店预览
	 * https://runsa.yuque.com/runsadev/ir5wcy/ke8fxn
	 */
	export const ipsPreview = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.IPS_PREVIEW);

	/**
	 * AC7.1获取属性列表（最新）
	 * /admin/getAttrTreeList
	 */
	export const getAttrTreeList = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.GET_ATTR_TREE_LIST);

	/**
	 * VG20.1虚拟店长统计列表导出
	 * /admin/v/report/guider/list/export
	 */
	export const guiderCountListExport = apiWithTokenLoaderExport<ItemObject>(IPAPIConfig.APIResURLConfig.GUIDER_COUNT_LIST_EXPORT);

	/**
	 * CXA9.促销活动同步中台活动时间
	 * https://runsa.yuque.com/runsadev/ir5wcy/hrnc7b
	 */
	export const syncOmsTime = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SYNCHRO_OMS_TIME);

	/**
	 * AS74.分账配置模板下载
	 * https://runsa.yuque.com/runsadev/ir5wcy/fx1d1w
	 */
	export const splitTemplateExport = apiWithTokenLoaderExport<ItemObject>(IPAPIConfig.APIResURLConfig.SPLIT_MONEY_TEMPLATE_EXPORT);

	/**
	 * AS72.网点分账配置
	 * https://runsa.yuque.com/runsadev/ir5wcy/oe1hgv
	 */
	export const splitConfigSave = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SPLIT_MONEY_CONFIG_SAVE);

	/**
	 * AS73.网点分账配置详情
	 * https://runsa.yuque.com/runsadev/ir5wcy/wdg0y3
	 */
	export const splitConfigInfo = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SPLIT_MONEY_CONFIG_INFO);

	/**
	 * ALV2.1.后台直播列表导出
	 * https://runsa.yuque.com/runsadev/ir5wcy/os5px3
	 */
	export const videoListExport = apiWithTokenLoaderExport<ItemObject>(IPAPIConfig.APIResURLConfig.VIDEO_LIST_EXPORT);

	/**
	 * SK1.2.秒杀活动列表导出
	 * https://runsa.yuque.com/runsadev/ir5wcy/nug1rc
	 */
	export const skillListExport = apiWithTokenLoaderExport<ItemObject>(IPAPIConfig.APIResURLConfig.SKILL_LIST_EXPORT);

	/**
	 * AS76.根据店铺ID获取网点组信息
	 * https://runsa.yuque.com/runsadev/ir5wcy/vsdant
	 */
	export const siteGroupConfig = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SITE_GROUP_CONFIG);

	/**
	 * ZX-GS17.查询CRM优惠卷列表
	 * https://runsa.yuque.com/runsadev/ir5wcy/dq8d8q
	 */
	export const couponList = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.COUPON_LIST);

	/**
	 * ZX-GS16商品详情页面配置详情
	 * https://runsa.yuque.com/runsadev/ir5wcy/gps5gh
	 */
	export const productInfoConfig = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.PRODUCT_INFO_CONFIG);

	/**
	 * ZX-GS15.商品详情页面配置保存
	 * /admin/product/info/config/save
	 */
	export const productInfoConfigSave = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.PRODUCT_INFO_CONFIG_SAVE);

	/**
	 * ZXA22.云镜店分享配置
	 * https://runsa.yuque.com/runsadev/ir5wcy/hx285s
	 */
	export const shareConfigSave = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SHARE_CONFIG_SAVE);

	/**
	 * ZXA23.分享图配置详情
	 * https://runsa.yuque.com/runsadev/ir5wcy/om0yry
	 */
	export const shareConfigInfo = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SHARE_CONFIG_INFO);

	/**
	 * ZXA24.网点分享图配置
	 * https://runsa.yuque.com/runsadev/ir5wcy/dxuz3w
	 */
	export const siteShareConfigSave = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SITE_SHARE_CONFIG_SAVE);

	/**
	 * ZXA25.网点分享图配置详情
	 * /admin/site/share/config/info
	 */
	export const siteShareConfigInfo = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SITE_SHARE_CONFIG_INFO);

	/**
	 * FX2.分销策略列表
	 * https://runsa.yuque.com/runsadev/ir5wcy/ucn26e
	 */
	export const newDistributionList = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.NEW_DISTRIBUTION_LIST);

	/**
	 * FX1.添加分销策略
	 * https://runsa.yuque.com/runsadev/ir5wcy/xu51qm
	 */
	export const newDistributionSave = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.NEW_DISTRIBUTION_SAVE);

	/**
	 * FX6.启用/禁用分销策略
	 * https://runsa.yuque.com/runsadev/ir5wcy/igls96
	 */
	export const newDistributionUpdateState = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.NEW_DISTRIBUTION_UPDATE_STATE);

	/**
	 * FX7.删除分销策略
	 * https://runsa.yuque.com/runsadev/ir5wcy/wztg7h
	 */
	export const newDistributionDel = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.NEW_DISTRIBUTION_DEL);

	/**
	 * FX5.设置默认策略
	 * https://runsa.yuque.com/runsadev/ir5wcy/kfhklv
	 */
	export const newDistributionSetDefault = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.NEW_DISTRIBUTION_SET_DEFAULT);

	/**
	 * FX3.配置分销策略
	 * https://runsa.yuque.com/runsadev/ir5wcy/mzh91l
	 */
	export const newDistributionIndoEdit = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.NEW_DISTRIBUTION_INFO_EDIT);

	/**
	 * FX4.分销策略配置详情
	 * https://runsa.yuque.com/runsadev/ir5wcy/fd24r2
	 */
	export const newDistributionDetail = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.NEW_DISTRIBUTION_DETAIL);

	/* FX22.会员等级
	http://yapi.tenyun.runsasoft.com/mock/68/admin/buyer/level
	*/
	export const buyerLevel = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.BUYER_LEVEL);

	/**
	 * FX24.分销申请二维码
	 * @see https://yapi.tenyun.runsasoft.com/project/68/interface/api/6470
	 */

	export const distributionQrcodeExport = apiWithTokenLoaderExport<ItemObject>(IPAPIConfig.APIResURLConfig.DISTRIBUTION_QRCODE_EXPORT);
	/**
	 * FX8.特殊商品佣金配置列表
	 * https://runsa.yuque.com/runsadev/ir5wcy/gdzp12
	 */
	export const proCommPolicyList = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.PRODUCT_COMMISSION_POLICY_LIST);

	/**
	 * FX10.特殊商品佣金配置删除
	 * https://runsa.yuque.com/runsadev/ir5wcy/hg32nd
	 */
	export const productCommissionDelete = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.PRODUCT_COMMISSION_DELETE);

	/**
	 * FX9.特殊商品佣金比例保存
	 * https://runsa.yuque.com/runsadev/ir5wcy/pe7slg
	 */
	export const productCommissionSave = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.PRODUCT_COMMISSION_SAVE);

	/**
	 * FX11.特殊商品数据模板导出
	 * https://runsa.yuque.com/runsadev/ir5wcy/rahr84
	 */
	export const proComTemplateExport = apiWithTokenLoaderExport<ItemObject>(IPAPIConfig.APIResURLConfig.PRODUCT_COMMISSION_TEMPLATE_EXPORT);

	/**
	 * FX12.特殊商品佣金配置导入
	 * https://runsa.yuque.com/runsadev/ir5wcy/vao2ph
	 */
	export const proComTemplateImport = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.PRODUCT_COMMISSION_TEMPLATE_IMPORT);

	/**
	 * FX13.分销员管理列表
	 * https://runsa.yuque.com/runsadev/ir5wcy/unq9n9
	 */
	export const dsitributorList = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.DISTRIBUTOR_LIST);

	/**
	 * FX14.分销员详情
	 * https://runsa.yuque.com/runsadev/ir5wcy/wi23hd
	 */
	export const dsitributorInfo = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.DISTRIBUTOR_INFO);

	/**
	 * R7.角色标签下拉框数据
	 * https://runsa.yuque.com/runsadev/ir5wcy/ly1m5t
	 */
	export const guiderTagList = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.GUIDER_TAG_LIST);

	/**
	 * AS77.获取云镜店入驻网点组信息(树结构)
	 * https://runsa.yuque.com/runsadev/ir5wcy/bsvz3x
	 */
	export const realSiteList = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.REAL_SITE_LIST);

	/**
	 * FX17.分销员策略批量变更数据导出
	 * https://runsa.yuque.com/runsadev/ir5wcy/gv2hah
	 */
	export const distributorChangeExport = apiWithTokenLoaderExport<ItemObject>(IPAPIConfig.APIResURLConfig.DISTRIBUTOR_CHANGE_EXPORT);

	/**
	 * FX15.分销员变更履历
	 * https://runsa.yuque.com/runsadev/ir5wcy/rfkdsg
	 */
	export const distributorChangeHistory = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.ISTRIBUTOR_CHANGE_HISTORY);

	/**
	 * FX16.修改分销员的分销策略
	 * https://runsa.yuque.com/runsadev/ir5wcy/fwy8el
	 */
	export const distributorChange = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.DISTRIBUTOR_CHANGE);

	/**
	 * R8.修改分销员申请策略信息
	 * https://runsa.yuque.com/runsadev/ir5wcy/medgmr
	 */
	export const guiderRequestPolicyChange = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.GUIDER_REQUEST_POLICY_CHANGE);

	/**
	 * VG15.1押金管理列表导出
	 * https://runsa.yuque.com/runsadev/ir5wcy/gtcs7a
	 */
	export const guiderDepositOutListExport = apiWithTokenLoaderExport<ItemObject>(IPAPIConfig.APIResURLConfig.GUIDER_DEPOSIT_OUT_LIST_EXPORT);

	/**
	 * FX19.分销员注销
	 * https://runsa.yuque.com/runsadev/ir5wcy/uklcmg
	 */
	export const distributorLogOut = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.DISTRIBUTOR_LOG_OUT);

	/**
	 * FX20.批量修改分销员状态
	 * https://runsa.yuque.com/runsadev/ir5wcy/lucgda
	 */
	export const distributorUpdateState = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.DISTRIBUTOR_UPDATE_STATE);

	/**
	 * ADN4.清除商品详情缓存
	 * https://runsa.yuque.com/runsadev/ir5wcy/ing6ez
	 */
	export const clearProductCodesCache = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.CLEAR_PRODUCTCODES_CACHE);

	/**
	 * A25.查询所有标签
	 * https://runsa.yuque.com/runsadev/ir5wcy/lkozna
	 */
	export const customerLabel = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.CUSTOMER_LABEL);

	/**
	 * A26.抽签活动试算
	 * https://runsa.yuque.com/runsadev/ir5wcy/qgbpzl
	 */
	export const actExtractionTrial = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.ACT_EXTRACTION_TRIAL);

	/**
	 * A27.顾客信息上传
	 * @see https://runsa.yuque.com/runsadev/ir5wcy/xhhm7g
	 */
	export const customerCstIdImport = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.CUSTOMER_CSTID_IMPORT);

	/**
	 * PQ2. 发布期刊修改状态
	 * @see https://runsa.yuque.com/runsadev/ir5wcy/38371368
	 */
	export const qikanStateEdit = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.QIKAN_STATE_EDIT);

	/**
	 * FX21.特殊商品全部删除
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/904
	 */
	export const productCommissionAllDelete = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.PRODUCT_COMMISSION_CLEAR);

	/**
	 * AP-AP1.商品管理列表
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/494
	 */
	export const siteProductList = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SITE_PRODUCT_LIST);

	/**
	 * AP-AP2.编辑商品
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/500
	 */
	export const siteProductAdd = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SITE_PRODUCT_ADD);

	/**
	 * AP-AP3.商品管理详情
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/858
	 */
	export const siteProductInfo = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SITE_PRODUCT_INFO);

	/**
	 * AP-AP4.同步库存
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/534
	 */
	export const siteProductStock = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SITE_PRODUCT_UPDATE_STOCK);

	/**
	 * AP-AP5.商品批量修改状态
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/538
	 */
	export const siteProductState = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SITE_PRODUCT_UPDATE_STATE);

	/**
	 * SZX1.菜单导航配置详情
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/844
	 */
	export const siteMenuNavInfo = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SITE_MENU_NAV_INFO);

	/**
	 * SZX2.菜单导航配置保存
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/846
	 */
	export const siteMenuNavSave = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SITE_MENU_NAV_SAVE);

	/**
	 * SZX3.HOT页配置详情
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/848
	 */
	export const siteHotInfo = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SITE_HOT_INFO);

	/**
	 * SZX4.HOT页配置保存
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/850
	 */
	export const siteHotSave = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SITE_SAVE);

	/**
	 * SZX5.商城页配置详情
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/854
	 */
	export const siteMallInfo = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SITE_MALL_INFO);

	/**
	 * SZX6.商城页配置保存
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/856
	 */
	export const siteMallSave = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SITE_SAVE);
	/**
	 * SZX6.商城、个人中心页布局保存
	 * @see https://yapi.tenyun.runsasoft.com/project/68/interface/api/3726
	 */
	export const componentLayoutSave = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.COMPONENT_LAYOUT_SAVE);
	/**
	 * SZX6.商城、个人中心页内容保存
	 * @see https://yapi.tenyun.runsasoft.com/project/68/interface/api/3724
	 */
	export const componentContentSave = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.COMPONENT_CONTENT_SAVE);

	/**
	 * SZX7.个人中心详情
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/860
	 */
	export const siteMemberCenterInfo = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SITE_MEMBER_CENTER_INFO);

	/**
	 * SZX8.个人中心保存
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/862
	 */
	export const siteMemberCenterSave = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SITE_SAVE);
	/* 闪屏广告详情 */
	export const flashAdDetail = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SITE_FLASH_ADV_INFO);
	/* 闪屏广告保存 */
	export const flashAdSave = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SITE_SAVE);
	/**
	 * SZX9.开屏广告配置详情
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/864
	 */
	export const siteOpenAdvInfo = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SITE_OPEN_ADV_INFO);

	/**
	 * SZX10.开屏广告配置保存
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/866
	 */
	export const siteOpenAdvSave = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SITE_OPEN_ADV_SAVE);

	/**
	 * SZX11.商品详情页配置详情
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/868
	 */
	export const siteProductInfoConfig = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SITE_PRODUCT_INFO_CONFIG);

	/**
	 * SZX12.商品详情页配置保存
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/870
	 */
	export const siteProductSaveConfig = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SITE_PRODUCT_SAVE_CONFIG);

	/**
	 * SZX13.分享图配置详情
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/872
	 */
	export const siteShareInfoConfig = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SITE_SHARE_INFO_CONFIG);

	/**
	 * SZX14.分享图配置保存
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/874
	 */
	export const siteShareSaveConfig = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SITE_SHARE_SAVE_CONFIG);

	/**
	 * SZX15.悬浮图标配置详情
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/876
	 */
	export const siteHoverIconInfo = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SITE_HOVER_ICON_INFO);

	/**
	 * SZX16.悬浮图标配置保存
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/878
	 */
	export const siteHoverIconSave = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SITE_HOVER_ICON_SAVE);

	/**
	 * AP-AP10.获取尺码对照表
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/970
	 */
	export const sitePicInfo = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SITE_CLASS_PIC_INFO);

	/**
	 * AP-AP9.保存尺码对照表
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/960
	 */
	export const sitePicSave = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SITE_CLASS_PIC_SAVE);

	/**
	 * AP-AP8.编辑分类状态
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/940
	 */
	export const siteProductClassEditState = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SITE_PRODUCT_CLASS_EDIT_STATE);

	/**
	 * AP-AP7.新增/编辑分类
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/938
	 */
	export const siteClassEdit = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SITE_CLASS_EDIT);

	/**
	 * AP-AP6.店铺分类列表
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/934
	 */
	export const siteClassList = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SITE_CLASS_LIST);

	/**
	 * AP-AP11.批量添加商品导入文件
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/982
	 */
	export const siteAddProductImport = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SITE_ADD_PRODUCT_IMPORT);

	/**
	 * AP-AP12.批量添加商品确认
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/986
	 */
	export const siteAddProductImportConfirm = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SITE_ADD_PRODUCT_IMPORT_CONFIRM);
	/**
	 * AP-AP12..批量导入上下架
	 * @see http://yapi.tenyun.runsasoft.com/mock/68/admin/product/change/state
	 */
	export const siteProductChangeState = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SITE_PRODUCT_CHANGE_STATE);

	/**
	 * SZX17.微页面配置详情
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/932
	 */
	export const siteMicroPageConfigInfo = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SITE_MICRO_PAGE_CONFIG_INFO);

	/**
	 * SZX18.微页面配置保存
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/936
	 */
	export const siteMicroPageConfigSave = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SITE_MICRO_PAGE_CONFIG_SAVE);

	/**
	 * SZX19.云商城网点组微页面详情
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/958
	 */
	export const siteMicroPageInfo = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SITE_MICRO_PAGE_INFO);

	/**
	 * SZX20.云商城网点组微页面保存
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/962
	 */
	export const siteMicroPageSave = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SITE_MICRO_PAGE_SAVE);

	/**
	 * SZX19.微页面列表
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/1032
	 */
	export const siteMicroPageList = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SITE_MICRO_PAGE_LIST);

	/**
	 * SZX20.删除微页面
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/1042
	 */
	export const siteMicroPageDelete = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SITE_MICRO_PAGE_DELETE);

	/**
	 * SZX21.启用/禁用微页面
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/1044
	 */
	export const siteMicroPageUpdateState = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SITE_MICRO_PAGE_UPDATE_STATE);

	/**
	 * SZX22.微页面配置预览
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/1048
	 */
	export const siteMicroPageConfigDisplay = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SITE_MICRO_PAGE_CONFIG_DISPLAY);

	/**
	 * SZX23.微页面暂存
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/1050
	 */
	export const siteMicroPageTempSave = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SITE_MICRO_PAGE_TEMP_SAVE);

	/**
	 * AP-AP2.编辑商品
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/500
	 */
	export const siteProductManageAdd = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SITE_PRODUCT_MANAGE_ADD);
	/**
	 * PIC.批量转图片地址
	 * @see https://yapi.tenyun.runsasoft.com/project/68/interface/api/5411
	 */
	export const picsChange = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.PICS_CHANGE);
	/**
	 * AP31.更新商品sku信息
	 * @see https://yapi.tenyun.runsasoft.com/project/68/interface/api/5423
	 */
	export const updateProductSkus = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.UPDATE_PRODUCT_SKUS);

	/**
	 * AP-AP4.同步数据
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/534
	 */
	export const siteProductStockUpdate = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SITE_PRODUCT_STOCK_UPDATE);

	/**
	 * AP-AP14.批量设置分类
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/1134
	 */
	export const siteProductClassUpdate = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SITE_PRODUCT_UPDATE_CLASS);

	/**
	 * AP-AP13.删除分类
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/1070
	 */
	export const siteProductClassDelete = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SITE_PRODUCT_CLASS_DELETE);

	/**
	 * AS78.获取参与分销网点列表
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/1010
	 */
	export const cloudDistributionSiteList = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.ClOUD_DISTRIBUTION_SITE_LIST);

	/**
	 * AS79.设置参与分销网点
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/1012
	 */
	export const cloudDistributionSiteAdd = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.ClOUD_DISTRIBUTION_SITE_ADD);

	/**
	 * SZX24.HOT页配置预览
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/1148
	 */
	export const siteHotConfigDisplay = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SITE_HOT_CONFIG_DISPLAY);

	/**
	 * SZX25.商城页配置预览
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/1150
	 */
	export const siteMallConfigDisplay = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SITE_MALL_CONFIG_DISPLAY);

	/**
	 * SZX26.个人中心配置预览
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/1152
	 */
	export const siteMemberConfigDisplay = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SITE_MEMBER_CONFIG_DISPLAY);

	/**
	 * AS80.获取某虚拟网点的分销网点
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/1014
	 */
	export const cloudDistributionSiteGet = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.CLOUD_DISTRIBUTION_SITE_GET);

	/**
	 * AP-AP15.获取分类下的商品信息
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/1206
	 */
	export const siteClassProductList = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SITE_CLASS_PRODUCT_LIST);

	/**
	 * AS81.保存尺码对照表(新)
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/1232
	 */
	export const classSizeSaveNew = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.CLASS_SIZE_SAVE_NEW);

	/**
	 * AS82.获取尺码对照表(新)
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/1238
	 */
	export const classSizeGetNew = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.CLASS_SIZE_GET_NEW);

	/**
	 * SZX14.分享图配置保存
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/1252
	 */
	export const siteShareConfigEdit = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.Y_SITE_SHARE_CONFIG_SAVE);

	/**
	 * SZX13.分享图配置详情
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/1250
	 */
	export const siteShareConfigDetail = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.Y_SITE_SHARE_CONFIG_INFO);

	/**
	 * P24. 获取商品属性筛选列表
	 * @see http://yapi.tenyun.runsasoft.com/project/81/interface/api/1264
	 */
	export const productAttrList = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.PRODUCT_ATTR_LIST);

	/* AP-AP24. 根据商品货号更新商品价格 */
	export const productChangePrice = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.PRODUCT_CHANGE_PRICE);

	/* AP-AP25. 根据商品货号更新商品价格验证 */
	export const productChangeCheck = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.PRODUCT_CHANGE_CHECK);
	/**
	 * AS84.筛选项配置详情
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/1262
	 */
	export const classFilterList = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.CLASS_FILTER_LIST);

	/**
	 * AS83.一级筛选项配置保存
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/1260
	 */
	export const classFilterSave = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.CLASS_FILTER_SAVE);

	/**
	 * AS85.新增下级
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/1266
	 */
	export const classFilterSecondSave = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.CLASS_FILTER_SECOND_SAVE);

	/**
	 * AS86.筛选项状态修改
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/1268
	 */
	export const classFilterStateChange = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.CLASS_FILTER_STATE_CHANGE);

	/**
	 * AS87.筛选项分类删除
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/1270
	 */
	export const classFilterStateDelete = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.CLASS_FILTER_DELETE);

	/**
	 * AS88.筛选项配置详情
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/1272
	 */
	export const classFilterInfo = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.CLASS_FILTER_INFO);

	/**
	 * A28.获取互动活动列表
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/1454
	 */
	export const interactiveList = apiClientCrmLoader<ItemObject>(IPAPIConfig.APIResURLConfig.INTER_ACTIVE_LIST);

	/**
	 * AP-AP16.店铺分类列表(新)
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/1478
	 */
	export const siteClassListNew = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SITE_CLASS_LIST_NEW);

	/**
	 * AP-AP17.新增/编辑分类(新)
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/1482
	 */
	export const siteClassEditNew = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SITE_CLASS_EDIT_NEW);

	/**
	 * AP-AP18.编辑分类状态(新)
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/1484
	 */
	export const siteClassEditStateNew = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.PRODUCT_CLASS_EDITSTATE_NEW);

	/**
	 * AP-AP19.删除分类(新)
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/1486
	 */
	export const productClassDeleteNew = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.PRODUCT_CLASS_DELETE_NEW);

	/**
	 * AP-AP27.编辑一级分类
	 * @see http://yapi.tenyun.runsasoft.com/mock/68/admin/y/first/class/edit
	 */
	export const firstClassEdit = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.FIRST_CLASS_EDIT);

	/**
	 * AP-AP20.绑定主档分类
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/1488
	 */
	export const productRelateAttr = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.PROUCT_RELATE_ATTRIBUTE);

	/**
	 * VG32.二级列表
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/1520
	 */
	export const guiderSecondList = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.GUIDER_SECOND_LIST);

	/**
	 * VG33.解除一二级关系
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/1522
	 */
	export const guiderSecondRemove = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.GUIDER_SECOND_REMOVE);

	/**
	 * VG34.二级明细列表
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/1524
	 */
	export const reportGuiderChildList = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.REPORT_GUIDER_CHILD_LIST);

	/**
	 * CP1.优惠券列表
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/1540
	 */
	export const siteCouponList = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SITE_COUPON_LIST);

	/**
	 * CP2.添加券
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/1542
	 */
	export const siteCouponAdd = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SITE_COUPON_ADD);

	/**
	 * 获取筛选条件详情接口(访问crm接口)
	 */
	export const customerCondition = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.CUSTOME_CONDITION);

	/**
	 * CP7.CRM优惠券详情
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/1552
	 */
	export const crmCouponIfo = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.CRM_COUPON_INFO);

	/**
	 * CP3.优惠券详情
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/1544
	 */
	export const siteCouponInfo = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SITE_COUPON_INFO);

	/**
	 * CP4.删除优惠券
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/1546
	 */
	export const siteCouponDelete = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SITE_COUPON_DELETE);

	/**
	 * CP5.批量上下架
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/1548
	 */
	export const siteCouponUpdateState = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SITE_COUPON_UPDATE_STATE);

	/**
	 * CP6.关联商品列表
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/1550
	 */
	export const siteCouponProductList = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SITE_COUPON_PRODUCT_LIST);
	/**
	 * AP28.商品管理列表导出
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/3460
	 * */
	export const productListExport = apiWithTokenLoaderExport<ItemObject>(IPAPIConfig.APIResURLConfig.PRODUCT_LIST_EXPORT);
	/**
	 * CP9.刷新关联商品
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/1558
	 */
	export const siteCouponUpdateProduct = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SITE_COUPON_UPDATE_PRODUCT);

	/**
	 * AP-AP21.可领券列表
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/1562
	 */
	export const siteProductCouponList = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SITE_PRODUCT_COUPON_LIST);

	/**
	 * CP8.关联商品列表导出
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/1556
	 */
	export const siteCouponProductExport = apiWithTokenLoaderExport<ItemObject>(IPAPIConfig.APIResURLConfig.SITE_COUPON_PRODUCT_EXPORT);

	/**
	 * CP10.批量刷新上架券关联商品
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/1566
	 */
	export const siteCouponOpenUpdate = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SITE_COUPON_OPEN_UPDATE);

	/**
	 * AS92.自定义尺码对照表详情
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/1574
	 */
	export const sizeMappingInfo = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SIZE_MAPPING_INFO);

	/**
	 * AS90.保存自定义尺码对照表
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/1570
	 */
	export const sizeMappingSave = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SIZE_MAPPING_SAVE);

	/**
	 * AS91.上传尺码对照表
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/1572
	 */
	export const sizeMappingUpload = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SIZE_MAPPING_UPLOAD);

	/**
	 * AS93.尺码对照清楚全部
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/1576
	 */
	export const sizeMappingClearAll = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SIZE_MAPPING_CLEAR_ALL);

	/**
	 * AS95.删除当前尺码对照表
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/1602
	 */
	export const sizeMappingDelete = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.SIZE_MAPPING_DELETE);

	/**
	 * TAG1.商品角标列表
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/1664
	 */
	export const tagMappingList = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.TAG_MAPPING_LIST);

	/**
	 * TAG5.角标保存
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/1664
	 */
	export const tagMappingSave = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.TAG_MAPPING_SAVE);

	/**
	 * TAG3.角标批量修改状态
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/1680
	 */
	export const tagMappingUpdateState = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.TAG_MAPPING_UPDATE_STATE);

	/**
	 * TAG4.角标详情
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/1690
	 */
	export const tagMappingInfo = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.TAG_MAPPING_INFO);

	/**
	 * AP-AP22.商品角标列表
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/1706
	 */
	export const productMappingTagList = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.PRODUC_MAPPING_TAGLIST);

	/**
	 * AP-AP23.商品角标删除
	 * @see http://yapi.tenyun.runsasoft.com/project/68/interface/api/1714
	 */
	export const productMappingTagDelete = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.PRODUC_MAPPING_TAGDELETE);

	/**
	 * SKG1. 秒杀组活动列表
	 * https://wiki.qingger.cn/pages/viewpage.action?pageId=19103800
	 */
	export const secKillGroupList = apiWithTokenLoader<ListCountInfo<SeckillGroupList>>(IPAPIConfig.APIResURLConfig.ADMIN_SEC_KILL_GROUP_LIST);
	/**
	 * SKG2.秒杀组适用网点列表
	 * http://yapi.tenyun.runsasoft.com/project/68/interface/api/1754
	 */
	export const secKillGroupSiteList = apiWithTokenLoader<ListCountInfo<SecKillGroupSiteList>>(IPAPIConfig.APIResURLConfig.ADMIN_SEC_KILL_GROUP_SITE_LIST);
	/**
	 * SKG3.秒杀组销售量列表
	 * http://yapi.tenyun.runsasoft.com/project/68/interface/api/1762
	 */
	export const secKillGroupOrderList = apiWithTokenLoader<ListCountInfo<SecKillGroupOrderDetailList>>(IPAPIConfig.APIResURLConfig.ADMIN_SEC_KILL_GROUP_ORDER_LIST);

	/**
	 * SKG4.秒杀组状态修改
	 * http://yapi.tenyun.runsasoft.com/project/68/interface/api/1762
	 */
	export const secKillGroupStateEdit = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.ADMIN_SEC_KILL_GROUP_STATE_EDIT);

	/**
	 * SKG5.秒杀组详情
	 * http://yapi.tenyun.runsasoft.com/mock/68/admin/sec/kill/group/info
	 */
	export const secKillGroupInfo = apiWithTokenLoader<ISecKillGroupForm>(IPAPIConfig.APIResURLConfig.ADMIN_SEC_KILL_GROUP_INFO);

	/**
	 * SKG6.秒杀组保存
	 * http://yapi.tenyun.runsasoft.com/project/68/interface/api/1786
	 */
	export const secKillGroupSave = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.ADMIN_SEC_KILL_GROUP_SAVE);

	/**
	 * SKG7.修改秒杀组商品信息(价格/库存)
	 * http://yapi.tenyun.runsasoft.com/project/68/interface/api/1804
	 */
	export const secKillGroupProductEdit = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.ADMIN_SEC_KILL_GROUP_PRODUCT_EDIT);

	/**
	 * SKG8.删除秒杀组商品信息(下架)
	 * http://yapi.tenyun.runsasoft.com/project/68/interface/api/1812
	 */
	export const secKillGroupProductDel = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.ADMIN_SEC_KILL_GROUP_PRODUCT_DEL);

	/**
	 * SKG11.删除秒杀组
	 * http://yapi.tenyun.runsasoft.com/project/68/interface/api/1996
	 */
	export const secKillGroupDel = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.ADMIN_SEC_KILL_GROUP_DEL);
	/**
	 * TAG2.角标关联商品列表
	 * http://yapi.tenyun.runsasoft.com/project/68/interface/api/1672
	 */
	export const tagProductList = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.ADMIN_TAG_PRODUCT_LIST);

	/**
	 * SKG7-1.批量修改秒杀组商品信息(价格/库存)
	 * http://yapi.tenyun.runsasoft.com/project/68/interface/api/2014
	 */
	export const secKillGroupProductsEdit = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.ADMIN_SEC_KILL_GROUP_PRODUCTS_EDIT);

	/**
	 * SK13.生成秒杀/秒杀组链接
	 * http://yapi.tenyun.runsasoft.com/project/68/interface/api/2032
	 */
	export const adminCreateSKUrl = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.ADMIN_CREATE_SK_URL);

	/* PTA9. 生成拼团链接 */
	export const adminCreatePTUrl = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.ADMIN_CREATE_PT_URL);

	/*  ALV19. 生成直播链接
		http://yapi.tenyun.runsasoft.com/mock/68/admin/create/live/url
		*/
	export const adminCreateLiveUrl = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.ADMIN_CREATE_LIVE_URL);
	/**
	 * VG35.二级列表下载
	 * http://yapi.tenyun.runsasoft.com/project/68/interface/api/2052
	 */

	export const adminVGuiderListExport = apiWithTokenLoaderExport<ItemObject>(IPAPIConfig.APIResURLConfig.ADMIN_V_GUIDER_LIST_EXPORT);

	/**
	 * FX22.分销员管理列表下载
	 * http://yapi.tenyun.runsasoft.com/project/68/interface/api/2042
	 */
	export const dsitributorListExport = apiWithTokenLoaderExport<ItemObject>(IPAPIConfig.APIResURLConfig.DISTRIBUTOR_LIST_EXPORT);

	/**
	 * FX22.分销员管理列表下载
	 * http://yapi.tenyun.runsasoft.com/project/68/interface/api/2042
	 */
	export const getExcelDownloadUrl = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.GET_EXCEL_DOWNLOAD_URL);
	/**
	 * AREA0..区域排除店铺接口
	 * @see https://yapi.tenyun.runsasoft.com/project/68/interface/api/3604
	 */
	export const areaOutSiteList = apiWithTokenLoader<ListCountInfo<ItemObject>>(IPAPIConfig.APIResURLConfig.AREA_OUT_SITE_LIST);
	/**
	 * AREA1.区域列表
	 * @see https://yapi.tenyun.runsasoft.com/project/68/interface/api/3588
	 */
	export const areaList = apiWithTokenLoader<ListCountInfo<AreaListNew>>(IPAPIConfig.APIResURLConfig.AREA_LIST);
	/**
	 * AREA2.区域详情
	 * @see https://yapi.tenyun.runsasoft.com/project/68/interface/api/3596
	 */
	export const areaInfo = apiWithTokenLoader<AreaInfo>(IPAPIConfig.APIResURLConfig.AREA_INFO);
	/**
	 * AREA3.区域保存
	 * @see https://yapi.tenyun.runsasoft.com/project/68/interface/api/3612
	 */
	export const areaSave = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.AREA_SAVE);
	/**
	 * AREA4.区域删除
	 * @see https://yapi.tenyun.runsasoft.com/project/68/interface/api/3620
	 */
	export const areaDelete = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.AREA_DELETE);
	/**
	 * AREA4.店铺列表
	 * @see https://yapi.tenyun.runsasoft.com/project/68/interface/api/3628
	 */
	export const areaSiteList = apiWithTokenLoader<ListCountInfo<AreaSiteShopList>>(IPAPIConfig.APIResURLConfig.AREA_SITE_LIST);

	/**
	 * crm 品牌组列表
	 */
	export const crmSystemBrandGroup = apiClientCrmLoader<any>(IPAPIConfig.APIResURLConfig.CRM_SYSTEM_BRANDS_GROUP);
	/**
	 * AS102.搜索(推荐)关键词排序
	 * @see https://yapi.tenyun.runsasoft.com/project/68/interface/api/5100
	 */
	export const siteKeywordSort = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.SITE_KEYWORD_SORT);
	/**
	 * AS101.搜索(推荐)关键词列表
	 * @see https://yapi.tenyun.runsasoft.com/project/68/interface/api/5082
	 */
	export const siteKeywordList = apiWithTokenLoader<ListCountInfo<SearchKeyWord>>(IPAPIConfig.APIResURLConfig.SITE_KEYWORD_LIST);
	/**
	 * AS100.添加/编辑搜索(推荐)关键词
	 * @see https://yapi.tenyun.runsasoft.com/project/68/interface/api/5091
	 */
	export const siteKeywordSave = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.SITE_KEYWORD_SAVE);
	/**
	 * AS103.删除关键词(推荐)
	 * @see https://yapi.tenyun.runsasoft.com/project/68/interface/api/5109
	 */
	export const siteKeywordDelete = apiWithTokenLoader<APIRetSuccess>(IPAPIConfig.APIResURLConfig.SITE_KEYWORD_DELETE);
	/**
	 * AS104.小程序综合报表
	 * @see https://yapi.tenyun.runsasoft.com/project/68/interface/api/5838
	 */
	export const siteGroupVisitList = apiWithTokenLoader<ListCountInfo<SiteGroupVisit>>(IPAPIConfig.APIResURLConfig.SITE_GROUP_VISIT_LIST);


	/**
 * AS105.获取通用店铺列表
 * @see https://yapi.tenyun.runsasoft.com/project/68/interface/api/6653
 */
	export const adminSiteAuthList = apiWithTokenLoader<ListCountInfo<any>>(IPAPIConfig.APIResURLConfig.ADMIN_SITE_AUTH_LIST);



	/**
	 * AS106.添加网点组店铺
	 * @see https://yapi.tenyun.runsasoft.com/project/68/interface/api/6728
	 */
	export const adminSiteGroupSiteAdd = apiWithTokenLoader<ListCountInfo<any>>(IPAPIConfig.APIResURLConfig.ADMIN_SITE_GROUP_SITE_ADD);
  /**
     * AS107.获取云镜店入驻网点列表
     * @see https://yapi.tenyun.runsasoft.com/project/68/interface/api/6794
     */
   export const realSiteGroupSiteList = apiWithTokenLoader<ItemObject>(IPAPIConfig.APIResURLConfig.REAL_SITE_GROUP_SITE_LIST);
}
